// src/pages/ZillowClonePage.jsx

import React, { useState, useEffect, useRef, useMemo } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMap, CircleMarker} from "react-leaflet";
import 'leaflet/dist/leaflet.css';
import dayjs from 'dayjs';
import axios from 'axios';
import OfferModal from "./OfferModal.jsx";
import ListingItem from "../components/ListingItem.jsx";
import { useAuth } from "./authContext";
import offMarketProperties from '../assets/investorlift_all_properties.json';
import MarketToggle from './MarketToggle.jsx';
import { Range } from "react-range";
import { GeoJSON } from "react-leaflet"; // at top with other imports

// ...
import OffMarketModal from './OffMarketModal';



 import * as L from 'leaflet';
 import ExactAddressModal          from './ExactAddressModal'


 // ── ZIP polygon + geometry helpers ─────────────────────────────────────────────
// (Requires: import L from 'leaflet')

const ZIP_ARCGIS1 = (z) =>
  `https://services.arcgis.com/P3ePLMYs2RVChkJx/ArcGIS/rest/services/USA_ZIP_Code_Tabulation_Areas/FeatureServer/0/query?where=ZCTA5CE10='${z}'&outFields=ZCTA5CE10&outSR=4326&f=geojson`;
const ZIP_ARCGIS2 = (z) =>
  `https://services.arcgis.com/P3ePLMYs2RVChkJx/ArcGIS/rest/services/USA_ZIP_Codes/FeatureServer/0/query?where=ZIP='${z}'&outFields=ZIP&outSR=4326&f=geojson`;
const ZIP_TIGER = (z) =>
  `https://tigerweb.geo.census.gov/arcgis/rest/services/TIGERweb/State_County/MapServer/9/query?where=ZCTA5CE10='${z}'&outFields=ZCTA5CE10&outSR=4326&f=geojson`;

const fetchWithTimeoutWeb = (url, ms = 9000) =>
  Promise.race([
    fetch(url),
    new Promise((_, rej) => setTimeout(() => rej(new Error('timeout')), ms)),
  ]);

async function fetchZipGeoJSON(zip) {
  if (!/^\d{5}$/.test(String(zip))) throw new Error('ZIP must be 5 digits');
  const urls = [ZIP_ARCGIS1(zip), ZIP_ARCGIS2(zip), ZIP_TIGER(zip)];
  let lastErr = null;
  for (const u of urls) {
    try {
      const res = await fetchWithTimeoutWeb(u, 9000);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const gj = await res.json();
      if (gj && (gj.features?.length || gj.coordinates)) return gj;
    } catch (e) { lastErr = e; }
  }
  throw lastErr || new Error('No ZIP polygon found');
}

// GeoJSON → array of polygons (each polygon = [outerRing, hole1, ...])
function polysFromGeoJSON(geojson) {
  const feats = geojson?.type === 'FeatureCollection'
    ? geojson.features
    : geojson?.type === 'Feature'
      ? [geojson]
      : geojson
        ? [{ type: 'Feature', geometry: geojson }]
        : [];

  const out = [];
  feats.forEach(f => {
    const g = f.geometry;
    if (!g) return;
    if (g.type === 'Polygon') {
      out.push(
        g.coordinates.map(ring =>
          ring.map(([lng, lat]) => ({ latitude: lat, longitude: lng }))
        )
      );
    } else if (g.type === 'MultiPolygon') {
      g.coordinates.forEach(poly => {
        out.push(
          poly.map(ring =>
            ring.map(([lng, lat]) => ({ latitude: lat, longitude: lng }))
          )
        );
      });
    }
  });
  return out;
}

// Point-in-polygon (supports holes)
function pointInRing(pt, ring) {
  let inside = false;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const xi = ring[i].longitude, yi = ring[i].latitude;
    const xj = ring[j].longitude, yj = ring[j].latitude;
    const intersects = ((yi > pt.latitude) !== (yj > pt.latitude)) &&
      (pt.longitude < (xj - xi) * (pt.latitude - yi) / ((yj - yi) || 1e-12) + xi);
    if (intersects) inside = !inside;
  }
  return inside;
}
function pointInPolyWithHoles(pt, poly) {
  if (!poly?.length) return false;
  if (!pointInRing(pt, poly[0])) return false;     // outside outer ring
  for (let h = 1; h < poly.length; h++)            // in a hole?
    if (pointInRing(pt, poly[h])) return false;
  return true;
}
function pointInAnyPoly(pt, polys) {
  for (const poly of polys) if (pointInPolyWithHoles(pt, poly)) return true;
  return false;
}

// Fit a GeoJSON boundary on Leaflet and offset center for the right sidebar
function fitGeoJSONOnLeaflet(geojson, map, offsetX = 280) {
  try {
    if (!geojson || !map) return;
    const layer = L.geoJSON(geojson);
    const b = layer.getBounds();
    if (!b.isValid()) return;
    const c = b.getCenter();
    const z = map.getZoom();
    const pt = map.project(L.latLng(c.lat, c.lng), z);
    const shifted = L.point(pt.x - offsetX, pt.y);
    map.setView(map.unproject(shifted, z), z, { animate: true });
    map.fitBounds(b, { padding: [20, 20] });
  } catch {}
}
// Fix leaflet default icon issues
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl:
    'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl:
    'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl:
    'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});
// map your dropdown labels → Realtor16 “type” strings
const PROPERTY_TYPE_MAP = {
  "Houses":       "single_family",
  "Townhomes":    "townhomes",
  "Multi-Family": "multi_family",
  "Condos/Co-Ops":"condos",
  "Lots/Land":    "land",
  "Apartments":   "multi_family",
  "Manufactured": "mobile"
};
// top-level, after imports
const ChangeMapView = ({ center, zoom }) => {
  const map = useMap();
  useEffect(() => {
    if (!map || !center) return;
    map.setView(center, zoom ?? map.getZoom(), { animate: true });
  }, [map, center, zoom]);
  return null;
};
// ----- RapidAPI config -----
const RAPIDAPI_HOST = 'realtor16.p.rapidapi.com';
const RAPIDAPI_KEY  = '510e42edf9msh1028fbc335ec8e9p12456ejsn237f189ede04';
const API_HEADERS = {
  "x-rapidapi-host": RAPIDAPI_HOST,
  "x-rapidapi-key":  RAPIDAPI_KEY,
};

// Build base/coordinate endpoints from a mode
const isRentMode = (t) => String(t || '').toLowerCase().includes('rent');
const API_BASE = "https://realtor16.p.rapidapi.com";

export function makeBaseUrl(listingType) {
  switch (listingType) {
    case "For Rent": return `${API_BASE}/search/forrent`;
    case "Sold":     return `${API_BASE}/search/forsold`;
    default:         return `${API_BASE}/search/forsale`;
  }
}
const isNum = (v) => typeof v === 'number' && Number.isFinite(v);
export function makeCoordUrl(listingType) {
  switch (listingType) {
    case "For Rent": return `${API_BASE}/search/forrent/coordinates`;
    case "Sold":     return `${API_BASE}/search/forsold/coordinates`;
    default:         return `${API_BASE}/search/forsale/coordinates`;
  }
}


// Coordinate-search URL (near-me queries)

// ----- Gentle global rate-limit + 429 backoff + single-flight de-dupe -----
const inflight = new Map();
const keyFor = (url, params) => `${url}::${JSON.stringify(params||{})}`;
const sleep = (ms) => new Promise(r => setTimeout(r, ms));
const MAX_RPS = 1;
let nextFreeAt = 0;

const waitForSlot = async () => {
  const now = Date.now();
  const wait = Math.max(0, nextFreeAt - now);
  if (wait) await sleep(wait);
  nextFreeAt = Date.now() + Math.ceil(1000 / MAX_RPS);
};

async function apiGet(url, params = {}, headers = API_HEADERS) {
  const key = keyFor(url, params);
  if (inflight.has(key)) return inflight.get(key);

  const run = (async () => {
    await waitForSlot();
    let attempt = 0;
    while (attempt < 4) {
      try {
        return await axios.get(url, { params, headers });
      } catch (err) {
        if (err?.response?.status === 429) {
          const retryAfter = Number(err.response?.headers?.["retry-after"]);
          const backoff = Number.isFinite(retryAfter) ? retryAfter : Math.min(8, 2 ** attempt);
          await sleep(backoff * 1000);
          attempt++;
          continue;
        }
        throw err;
      }
    }
    throw new Error("429 after retries");
  })();

  inflight.set(key, run);
  try { return await run; } finally { inflight.delete(key); }
}
  // 1️⃣ Helper to build a blue-dot + label icon entirely inline


const SF_PHRASES = [
  'owner financing','seller financing','seller finance','seller financed',
  'seller will finance','owner will finance','seller carry','seller carryback',
  'seller will carry','swc','owc','owner will carry','carry back','carryback','carry-back',
  'contract for deed','installment sale','installment contract',
  'agreement for deed','land contract',
  'wraparound mortgage','wrap around mortgage','wrap mortgage','wrap',
  'aitd','all-inclusive trust deed',
  'lease purchase','lease-purchase','lease option','lease-option',
  'rent to own','rent-to-own','rent 2 own',
  'private financing available','will consider terms','flexible seller terms',
  'no bank qualifying','no bank needed','easy qualification'
];

function detectSellerFinancing(text = "") {
  const t = String(text).toLowerCase();
  const matches = SF_PHRASES.filter(p => {
    const safe = p.replace(/[-/\\^$*+?.()|[\]{}]/g, "\\$&");
    const rx = new RegExp(`(^|\\s|[(),.;:!?'"]+)${safe}($|\\s|[(),.;:!?'"]+)`, "i");
    return rx.test(t);
  });
  return { isSellerFinanced: matches.length > 0, score: matches.length, matches };
}

function collectTextFields(prop) {
  const fields = [
    prop?.description?.text,
    prop?.description?.public_remarks,
    prop?.public_remarks,
    prop?.publicRemarks,
    prop?.remarks,
    prop?.description?.description,
    prop?.title,
    prop?.summary,
    prop?.marketing_description,
    prop?.remarks_raw,
    prop?.property_description,
  ];
  return fields.filter(Boolean).join(" \n ");
}

// Normalizes RapidAPI property → your listing shape
// Normalizes RapidAPI property → your listing shape
function mapNewPropertyToOld(prop, { listingMode = "For Sale" } = {}) {
  const addr = prop?.location?.address ?? {};
  const countyName = prop?.location?.county?.name ?? "";
  const mergedText = collectTextFields(prop);

  const det = (prop?.seller_financed || prop?.sellerFinanced)
    ? { isSellerFinanced: true, score: 2, matches: ["explicit flag"] }
    : detectSellerFinancing(mergedText);

  const listedDateRaw =
    prop?.list_date || prop?.last_update_date || prop?.last_update ||
    prop?.listed_date || prop?.date_posted || null;

  // 🔽 NEW: try common sold date fields
  const soldDateRaw =
    prop?.sold_date || prop?.last_sold_date || prop?.date_sold || null;

  const listedDate   = dayjs(listedDateRaw || new Date());
  const daysOnMarket = dayjs().diff(listedDate, "day");

  const price = prop?.list_price ?? prop?.price ?? prop?.rent ?? prop?.price_min ?? null;

  const rawType  = prop?.description?.type || prop?.prop_status || prop?.status || "";
  const niceType = String(rawType).replace(/_/g, " ");

  const status =
    listingMode === "For Rent"
      ? (prop?.status === "for_rent" ? "For Rent" : (prop?.status || "For Rent"))
      : (prop?.status === "for_sale" ? "Active"   : (prop?.status || "Active"));

  const baths =
    Number(prop?.description?.baths_consolidated) ??
    Number(prop?.description?.baths) ??
    Number(prop?.baths) ?? 0;

  const rawHref = prop?.primary_photo?.href || prop?.photos?.[0]?.href || "";
  const imgSrc  = rawHref ? rawHref.replace(/(\.[^/.]+)$/, "-w2048_h1536$1") : "";

  return {
    id:               prop?.listing_id ?? prop?.property_id,
    property_id:      prop?.property_id,
    price,
    bedrooms:         prop?.description?.beds ?? prop?.beds ?? null,
    bathrooms:        Number.isFinite(baths) ? baths : null,
    daysOnMarket,
    formattedAddress: [addr?.line, addr?.city, addr?.state_code, addr?.postal_code].filter(Boolean).join(", "),
    latitude:         addr?.coordinate?.lat ?? null,
    longitude:        addr?.coordinate?.lon ?? null,
    propertyType:     niceType,
    imgSrc,
    name:             prop?.description?.name || addr?.line || "",
    state:            addr?.state_code ?? "",
    city:             addr?.city ?? "",
    zipCode:          addr?.postal_code ?? "",
    county:           countyName,
    listedDate:       listedDateRaw || null,
    soldDate:         soldDateRaw || null,            // ← 🔹 expose soldDate
    status,
    sellerFinanced:   !!det.isSellerFinanced,
    sellerFinScore:   Number.isFinite(det?.score) ? det.score : 0,
    sellerFinMatches: Array.isArray(det?.matches) ? det.matches : [],
    remarks:          mergedText,
  };
}

function cryptoRandomId() {
  // tiny fallback
  return Math.random().toString(36).slice(2) + Date.now().toString(36);
}

const parseNumber = (val) => Number(String(val).replace(/[^0-9.]/g, "")) || 0;

function calculateMortgage(price) {
  const n = typeof price === "number" ? price : parseNumber(price);
  if (!Number.isFinite(n) || n <= 0) return null;
  const P = n * 0.8;      // 20% down
  const I = 6.5 / 100 / 12;
  const N = 30 * 12;
  return Math.round(P * I * (Math.pow(1 + I, N)) / (Math.pow(1 + I, N) - 1));
}

function estimateFMR(bedrooms) {
  switch (bedrooms) {
    case 1: return 1207;
    case 2: return 1355;
    case 3: return 1781;
    case 4: return 2086;
    default: return 1105;
  }
}

function calculateCashflow(listing) {
  const m = calculateMortgage(listing?.price);
  if (m == null) return null;
  return estimateFMR(Number(listing?.bedrooms || 0)) - m;
}

// Leaflet marker icon with dynamic label + color
function createMarkerIcon(listing, mode = "price") {
  let label = "";
  let color = "#333";

  const isRent = /rent/i.test(String(listing?.status || ""));

  if (mode === "price") {
    const val = Number(listing?.price || 0).toLocaleString();
    label = isRent ? `$${val}/mo` : `$${val}`;
    color = "#1E90FF";
  }
 else if (mode === "days") {
    label = `${Number(listing?.daysOnMarket || 0)}d`;
    color = "#333";
  } else if (mode === "cashflow") {
    const cf = calculateCashflow(listing);
    if (cf == null) {
      label = "—";
      color = "#999";
    } else {
      label = `${cf < 0 ? "-" : ""}$${Math.abs(cf)}`;
      color = cf <= 0 ? "red" : cf < 200 ? "darkgoldenrod" : "green";
    }
  }

  const html = `
    <div style="
      display:inline-block;
      white-space:nowrap;
      background:#fff;
      border:1px solid #ddd;
      border-radius:12px;
      padding:4px 8px;
      box-shadow:0 1px 4px rgba(0,0,0,0.15);
      font:600 12px system-ui, -apple-system, Segoe UI, Roboto;
      color:${color};
    ">
      ${label}
    </div>`;

  return L.divIcon({
    html,
    className: "",
    // no fixed width — let it grow/shrink
    iconSize: null,
    iconAnchor: [0, 0], // top-left corner at point
  });
}
// ————— Realtor16 API config —————

const PriceDropdown = ({ priceType, setPriceType, priceMin, setPriceMin, priceMax, setPriceMax }) => {
  const [isOpen, setIsOpen] = useState(false);
  const toggleOpen = () => setIsOpen(!isOpen);

  const minOptions = [
    { value: "Any", label: "Min" },
    { value: "50000", label: "$50k" },
    { value: "100000", label: "$100k" },
    { value: "150000", label: "$150k" },
    { value: "200000", label: "$200k" },
    { value: "250000", label: "$250k" }
  ];
  const maxOptions = [
    { value: "Any", label: "Max" },
    { value: "300000", label: "$300k" },
    { value: "400000", label: "$400k" },
    { value: "500000", label: "$500k" },
    { value: "600000", label: "$600k" },
    { value: "700000", label: "$700k" }
  ];

  return (
    <div style={priceDropdownStyles.container}>
      <div style={priceDropdownStyles.selected} onClick={toggleOpen}>
        <span>Price</span>
        <span style={priceDropdownStyles.arrow}>▾</span>
      </div>
      {isOpen && (
        <div style={priceDropdownStyles.dropdownPanel}>
          <div style={priceDropdownStyles.toggleContainer}>
            <button 
              style={{
                ...priceDropdownStyles.toggleButton,
                backgroundColor: priceType === "List Price" ? "#007aff" : "#fff",
                color: priceType === "List Price" ? "#fff" : "#000"
              }}
              onClick={() => setPriceType("List Price")}
            >
              List Price
            </button>
            <button 
              style={{
                ...priceDropdownStyles.toggleButton,
                backgroundColor: priceType === "Monthly Payment" ? "#007aff" : "#fff",
                color: priceType === "Monthly Payment" ? "#fff" : "#000"
              }}
              onClick={() => setPriceType("Monthly Payment")}
            >
              Monthly Payment
            </button>
          </div>
          <div style={priceDropdownStyles.rangeContainer}>
            <select 
              value={priceMin} 
              onChange={(e) => setPriceMin(e.target.value)}
              style={priceDropdownStyles.select}
            >
              {minOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
            <select 
              value={priceMax} 
              onChange={(e) => setPriceMax(e.target.value)}
              style={priceDropdownStyles.select}
            >
              {maxOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>
          
          <button 
            style={priceDropdownStyles.applyButton}
            onClick={() => setIsOpen(false)}
          >
            Apply
          </button>
        </div>
      )}
    </div>
  );
};
const priceDropdownStyles = {
  container: {
    position: 'relative',
    flex: '0 0 auto',    // don’t shrink or grow
    minWidth: '140px',    // ensure at least this wide
  },
  selected: {
    padding: '8px 12px',
    fontSize: '14px',
    border: '1px solid #ccc',
    borderRadius: '6px',
    backgroundColor: '#fff',
    cursor: 'pointer',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  arrow: {
    marginLeft: '8px',
  },
  dropdownPanel: {
    position: 'absolute',
    top: '100%',
    left: 0,
    backgroundColor: '#fff',
    border: '1px solid #ccc',
    borderRadius: '6px',
    marginTop: '4px',
    zIndex: 1000,
    padding: '10px',
    width: '200px',       // widen panel
    boxSizing: 'border-box',
  },
  toggleContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    marginBottom: '10px',
  },
  toggleButton: {
    flex: 1,
    padding: '6px 8px',
    marginRight: '4px',
    border: '1px solid #007aff',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '13px',
  },
  rangeContainer: {
    display: 'flex',
    gap: '6px',
    marginBottom: '10px',
  },
  select: {
    flex: 1,
    padding: '6px 8px',
    fontSize: '13px',
    border: '1px solid #ccc',
    borderRadius: '4px',
    backgroundColor: '#fff',
    appearance: 'none',
  },
  applyButton: {
    width: '100%',
    padding: '8px',
    fontSize: '14px',
    backgroundColor: '#007aff',
    color: '#fff',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
  },
};


// ----- RadioDropdown Component for Listing Type -----

const RadioDropdown = ({ value, onChange, options }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [temp, setTemp] = useState(value);

  useEffect(() => { if (!isOpen) setTemp(value); }, [value, isOpen]);

  return (
    <div style={customDropdownStyles.container}>
      <div
        style={customDropdownStyles.selected}
        onClick={() => setIsOpen(o => !o)}
      >
        <span>{options.find(o => o.value === value)?.label || "Select"}</span>
        <span style={customDropdownStyles.arrow}>▾</span>
      </div>

      {isOpen && (
        <ul style={customDropdownStyles.options}>
          {options.map(opt => (
            <li
              key={opt.value}
              style={customDropdownStyles.option}
              onClick={() => setTemp(opt.value)}   // ← only update temp
            >
              <input
                type="radio"
                name="listingType"
                value={opt.value}
                checked={temp === opt.value}
                readOnly
                style={{ marginRight: 6 }}
              />
              {opt.label}
            </li>
          ))}
          <li style={{ textAlign: "center", padding: 8 }}>
            <button
              onClick={() => { setIsOpen(false); onChange(temp); }} // ← fire here
              style={{
                padding: "6px 12px",
                fontSize: 14,
                border: "none",
                borderRadius: 4,
                backgroundColor: "#007aff",
                color: "#fff",
                cursor: "pointer",
              }}
            >
              Apply
            </button>
          </li>
        </ul>
      )}
    </div>
  );
};

// re-use your existing customDropdownStyles:



// ----- CheckboxDropdown Component for Property Type -----
const CheckboxDropdown = ({ value, onChange, options }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [tempSelection, setTempSelection] = useState(value);
  const [isSelectedHovered, setIsSelectedHovered] = useState(false);

  useEffect(() => {
    setTempSelection(value);
  }, [value]);

  const toggleOpen = () => setIsOpen(!isOpen);
  const handleCheckboxChange = (optionValue) => {
    if (tempSelection.includes(optionValue)) {
      setTempSelection(tempSelection.filter(val => val !== optionValue));
    } else {
      setTempSelection([...tempSelection, optionValue]);
    }
  };
  const handleApply = () => {
    onChange(tempSelection);
    setIsOpen(false);
  };

  const selectedText = "Property Type";

  return (
    <div style={{ ...customDropdownStyles.container, width: '200px' }}>
      <div
        style={{
          ...customDropdownStyles.selected,
          backgroundColor: isSelectedHovered ? '#f8f8f8' : '#fff',
        }}
        onMouseEnter={() => setIsSelectedHovered(true)}
        onMouseLeave={() => setIsSelectedHovered(false)}
        onClick={toggleOpen}
      >
        <span>{selectedText}</span>
        <span style={customDropdownStyles.arrow}>▾</span>
      </div>
      {isOpen && (
        <ul
          style={{
            ...customDropdownStyles.options,
            left: '50%',
            transform: 'translateX(-50%)',
            width: '100%',
            maxHeight: 'none',
            overflowY: 'visible'
          }}
        >
          {options.map((option) => (
            <li key={option.value} style={customDropdownStyles.option}>
              <label style={{ display: "flex", alignItems: "center", cursor: "pointer" }}>
                <input
                  type="checkbox"
                  value={option.value}
                  checked={tempSelection.includes(option.value)}
                  onChange={() => handleCheckboxChange(option.value)}
                  style={{ marginRight: "5px" }}
                />
                {option.label}
              </label>
            </li>
          ))}
          <li style={{ padding: '10px', textAlign: 'center' }}>
            <button
              style={{
                display: 'block',
                width: '100%',
                backgroundColor: '#007aff',
                color: '#fff',
                border: 'none',
                borderRadius: '8px',
                padding: '8px 16px',
                fontSize: '16px',
                cursor: 'pointer'
              }}
              onClick={handleApply}
            >
              Apply
            </button>
          </li>
        </ul>
      )}
    </div>
  );
};

const BedroomsBathroomsDropdown = ({ value, onChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [tempBedrooms, setTempBedrooms] = useState(value.bedrooms || "Any");
  const [tempBathrooms, setTempBathrooms] = useState(value.bathrooms || "Any");
  const [isSelectedHovered, setIsSelectedHovered] = useState(false);

  useEffect(() => {
    setTempBedrooms(value.bedrooms || "Any");
    setTempBathrooms(value.bathrooms || "Any");
  }, [value]);

  const toggleOpen = () => setIsOpen(!isOpen);
  const handleApply = () => {
    onChange({ bedrooms: tempBedrooms, bathrooms: tempBathrooms });
    setIsOpen(false);
  };

  const optionStyle = (selected) => ({
    padding: '10px',
    fontSize: '16px',
    cursor: 'pointer',
    backgroundColor: selected ? '#e0f0ff' : '#fff',
    border: '1px solid #ccc',
    borderRadius: '4px',
    flex: '1',
    textAlign: 'center',
    margin: '2px'
  });

  const bedroomOptions = ["Any", "1+", "2+", "3+", "4+", "5+"];
  const bathroomOptions = ["Any", "1+", "2+", "3+", "4+", "5+"];

  const selectedText = "Beds & Baths";

  return (
    <div style={{ ...customDropdownStyles.container, width: '400px' }}>
      <div
        style={{
          ...customDropdownStyles.selected,
          backgroundColor: isSelectedHovered ? '#f8f8f8' : '#fff',
        }}
        onMouseEnter={() => setIsSelectedHovered(true)}
        onMouseLeave={() => setIsSelectedHovered(false)}
        onClick={toggleOpen}
      >
        <span>{selectedText}</span>
        <span style={customDropdownStyles.arrow}>▾</span>
      </div>
      {isOpen && (
        <div
          style={{
            ...customDropdownStyles.options,
            position: 'absolute',
            left: '50%',
            transform: 'translateX(-50%)',
            width: '200%',
            maxHeight: 'none',
            overflowY: 'visible',
            padding: '10px'
          }}
        >
          <div style={{ marginBottom: '10px' }}>
            <div style={{ fontWeight: 'bold', marginBottom: '5px' }}>Bedrooms</div>
            <div style={{ display: 'flex' }}>
              {bedroomOptions.map((option) => (
                <div
                  key={option}
                  style={optionStyle(tempBedrooms === option)}
                  onClick={() => setTempBedrooms(option)}
                >
                  {option}
                </div>
              ))}
            </div>
          </div>
          <div style={{ marginBottom: '10px' }}>
            <div style={{ fontWeight: 'bold', marginBottom: '5px' }}>Bathrooms</div>
            <div style={{ display: 'flex' }}>
              {bathroomOptions.map((option) => (
                <div
                  key={option}
                  style={optionStyle(tempBathrooms === option)}
                  onClick={() => setTempBathrooms(option)}
                >
                  {option}
                </div>
              ))}
            </div>
          </div>
          <div style={{ padding: '10px', textAlign: 'center' }}>
            <button
              style={{
                display: 'block',
                width: '100%',
                backgroundColor: '#007aff',
                color: '#fff',
                border: 'none',
                borderRadius: '8px',
                padding: '8px 16px',
                fontSize: '16px',
                cursor: 'pointer'
              }}
              onClick={handleApply}
            >
              Apply
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

const customDropdownStyles = {
  container: {
    position: 'relative',
    flex: '0 0 auto',      // don’t grow or shrink
    minWidth: '140px',      // same as PriceDropdown
    boxSizing: 'border-box',
  },
  selected: {
    padding: '8px 12px',    // same padding as PriceDropdown.selected
    fontSize: 14,           // same font size
    border: '1px solid #ccc',
    borderRadius: 4,        // same border radius
    backgroundColor: '#fff',
    cursor: 'pointer',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  arrow: {
    marginLeft: 8,
  },
  options: {
    position: 'absolute',
    top: '100%',
    left: 0,
    right: 0,
    backgroundColor: '#fff',
    border: '1px solid #ccc',
    borderRadius: 4,
    marginTop: 4,
    zIndex: 1000,
    listStyle: 'none',
    padding: 0,
    maxHeight: 200,
    overflowY: 'auto',
  },
  option: {
    padding: '8px 12px',
    cursor: 'pointer',
  },
};


/* =====================================
   OffMarketSearchBar Component
===================================== */
// This component now uses a controlled input to filter off-market properties automatically.
// It accepts offMarketProperties (defaulting to an empty array) and calls onSearch with the filtered results.

const OffMarketSearchBar = ({ offMarketProperties = [], onSearch }) => {
  const [offMarketSearchText, setOffMarketSearchText] = useState('');

  const filteredOffMarketProperties = useMemo(() => {
    if (!offMarketSearchText.trim()) return offMarketProperties;
    const query = offMarketSearchText.toLowerCase();
    return offMarketProperties.filter(listing => {
      return (
        (listing.city && listing.city.toLowerCase().includes(query)) ||
        (listing.state && listing.state.toLowerCase().includes(query))
      );
    });
  }, [offMarketSearchText, offMarketProperties]);

  useEffect(() => {
    if (onSearch) {
      onSearch(filteredOffMarketProperties);
    }
  }, [filteredOffMarketProperties, onSearch]);

  return (
    <div style={{ marginBottom: '30px' }}>
      <input
        type="text"
        placeholder="Search Off-Market by City or State"
        value={offMarketSearchText}
        onChange={(e) => setOffMarketSearchText(e.target.value)}
        style={{
          padding: '10px',
          fontSize: '16px',
          border: '1px solid #ccc',
          borderRadius: '8px',
          width: '100%',
        }}
      />
    </div>
  );
};



// --- PriceRangeDropdown.jsx (inline in same file is fine) ---
const currency = n =>
  n == null ? "Any" : `$${n.toLocaleString()}`;

const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v));

// Put this near your other dropdowns
// --- PriceRangeDropdown.jsx (replace your existing one) ---
const PriceRangeDropdown = ({
  minBound = 0,
  maxBound = 1_000_000,
  step = 5_000,
  value = [0, 1_000_000],
  onApply
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const safeMin = Number.isFinite(value?.[0]) ? value[0] : minBound;
  const safeMax = Number.isFinite(value?.[1]) ? value[1] : maxBound;
  const [vals, setVals] = useState([safeMin, safeMax]);

  useEffect(() => setVals([safeMin, safeMax]), [safeMin, safeMax]);

  const [min, max] = vals;
  const label = `$${min.toLocaleString()} – $${max.toLocaleString()}`;

  return (
    <div style={{ position:'relative', flex:'0 0 auto', minWidth: 220 }}>
      <div
        style={{
          padding:'8px 12px', fontSize:14, border:'1px solid #ccc',
          borderRadius:6, background:'#fff', cursor:'pointer',
          display:'flex', justifyContent:'space-between', alignItems:'center'
        }}
        onClick={() => setIsOpen(o=>!o)}
      >
        <span>{label}</span>
        <span style={{ marginLeft:8 }}>▾</span>
      </div>

      {isOpen && (
        <div
          style={{
            position:'absolute', top:'100%', left:0, zIndex:1000, width:300,
            background:'#fff', border:'1px solid #ccc', borderRadius:8,
            marginTop:4, padding:12, boxSizing:'border-box'
          }}
        >
          <div style={{ marginBottom: 12, fontWeight: 600 }}>{label}</div>

          <Range
  step={step}
  min={minBound}
  max={maxBound}
  values={vals}
  onChange={setVals}
  renderTrack={({ props, children }) => (
  <div
    {...props} // must keep handlers/ref here for drag to work
    style={{
      ...props.style,
      height: 28,
      display: 'flex',
      width: '100%',
      alignItems: 'center',
      position: 'relative',
    }}
  >
    <div
      style={{
        position: 'absolute',
        left: 0,
        right: 0,
        height: 4,
        borderRadius: 999,
        background: `linear-gradient(to right,
          #e5e5e5 0%,
          #e5e5e5 ${((vals[0]-minBound)/(maxBound-minBound))*100}%,
          #007aff  ${((vals[0]-minBound)/(maxBound-minBound))*100}%,
          #007aff  ${((vals[1]-minBound)/(maxBound-minBound))*100}%,
          #e5e5e5  ${((vals[1]-minBound)/(maxBound-minBound))*100}%,
          #e5e5e5 100%)`,
        pointerEvents: 'none', // <-- critical so the bar never steals drags
      }}
    />
    {children}
  </div>
)}
  renderThumb={({ props, isDragged, index }) => (
  <div
    {...props}
    style={{
      ...props.style,
      height: 18,
      width: 18,
      borderRadius: '50%',
      background: '#fff',
      border: '2px solid #007aff',
      boxShadow: isDragged ? '0 0 0 6px rgba(0,122,255,0.15)' : 'none',
      touchAction: 'none', // <-- prevents scroll from hijacking drag on touchpads/phones
    }}
    aria-label={index === 0 ? 'Minimum price' : 'Maximum price'}
  />
)}
/>

          <div style={{ display:'flex', gap:8, marginTop:12 }}>
            <div style={{ flex:1 }}>
              <div style={{ fontSize:12, color:'#555', marginBottom:4 }}>Min</div>
              <input
                type="number"
                value={vals[0]}
                min={minBound}
                max={vals[1]}
                step={step}
                onChange={(e)=> {
                  const v = Math.max(minBound, Math.min(Number(e.target.value)||0, vals[1]));
                  setVals([v, vals[1]]);
                }}
                style={{ width:'100%', padding:'6px 8px', border:'1px solid #ccc', borderRadius:6 }}
              />
            </div>
            <div style={{ flex:1 }}>
              <div style={{ fontSize:12, color:'#555', marginBottom:4 }}>Max</div>
              <input
                type="number"
                value={vals[1]}
                min={vals[0]}
                max={maxBound}
                step={step}
                onChange={(e)=> {
                  const v = Math.min(maxBound, Math.max(Number(e.target.value)||0, vals[0]));
                  setVals([vals[0], v]);
                }}
                style={{ width:'100%', padding:'6px 8px', border:'1px solid #ccc', borderRadius:6 }}
              />
            </div>
          </div>

          <button
            onClick={() => { onApply?.([vals[0], vals[1]]); setIsOpen(false); }}
            style={{
              width:'100%', marginTop:12, padding:'8px', fontSize:14,
              background:'#007aff', color:'#fff', border:'none', borderRadius:6, cursor:'pointer'
            }}
          >
            Apply
          </button>
        </div>
      )}
    </div>
  );
};

const priceRangeStyles = {
  container: { position:'relative', flex:'0 0 auto', minWidth:180 },
  selected: {
    padding:'8px 12px', fontSize:14, border:'1px solid #ccc', borderRadius:6,
    background:'#fff', cursor:'pointer', display:'flex', justifyContent:'space-between', alignItems:'center'
  },
  arrow: { marginLeft:8 },
  panel: {
    position:'absolute', top:'100%', left:0, zIndex:1000, width:280,
    background:'#fff', border:'1px solid #ccc', borderRadius:8, marginTop:4, padding:12, boxSizing:'border-box'
  },
  sliderWrap: { position:'relative', height:28, marginTop:10, marginBottom:6 },
  track: {
    position:'absolute', top:12, left:0, right:0, height:4, borderRadius:999, background:'#e5e5e5'
  },
  fill: {
    position:'absolute', top:12, height:4, borderRadius:999, background:'#007aff'
  },
  range: {
    position:'absolute', left:0, right:0, width:'100%', pointerEvents:'auto', background:'transparent',
    WebkitAppearance:'none', appearance:'none', height:28, margin:0
  },
  inputBox: { flex:1 },
  inputLabel: { fontSize:12, color:'#555', marginBottom:4 },
  numberInput: {
    width:'100%', padding:'6px 8px', border:'1px solid #ccc', borderRadius:6, fontSize:14, boxSizing:'border-box'
  },
  apply: {
    width:'100%', marginTop:12, padding:'8px', fontSize:14,
    background:'#007aff', color:'#fff', border:'none', borderRadius:6, cursor:'pointer'
  },
};

/* =====================================
   ChangeMapView Component
===================================== */


const ZillowSearchBar = ({ onSearch, onSelectExactListing, onNearMe, onBoundary }) => {
  const [input, setInput]               = useState("");
  const [suggestions, setSuggestions]   = useState([]);
  const [selectedCity, setSelectedCity] = useState("");
  const [selectedState, setSelectedState] = useState("");
   const [showExactModal, setShowExactModal] = useState(false)
// in ZillowSearchBar state
const [soldWithinDays, setSoldWithinDays] = useState(null); // null = any time
  const [listingType, setListingType]   = useState("For Sale");
  const [propertyType, setPropertyType] = useState([]);
  const [bedBath, setBedBath]           = useState({
    bedrooms: "Any",
    bathrooms: "Any",
  });

  const [priceType, setPriceType] = useState("List Price");
  const [priceMin, setPriceMin]   = useState("Any");
  const [priceMax, setPriceMax]   = useState("Any");
const [soldSort, setSoldSort] = useState("sold_date"); // API default

  const didMount = useRef(false);

  // build & fire a search, but allow overriding ANY of the base params
// inside ZillowSearchBar
// inside ZillowSearchBar
const getEffective = (override = {}) => {
  const lt    = override.listingType ?? listingType;
  const ptCsv = override.propertyType ?? (Array.isArray(propertyType) ? propertyType.join(",") : "");
  const bmin  = override.bedrooms  ?? (bedBath.bedrooms  !== "Any" ? Number(String(bedBath.bedrooms).replace("+","")) : undefined);
  const baMin = override.bathrooms ?? (bedBath.bathrooms !== "Any" ? Number(String(bedBath.bathrooms).replace("+","")) : undefined);
  const pType = override.priceType ?? priceType;
  const pMin  = override.priceMin  ?? (pType === "List Price" && priceMin !== "Any" ? Number(priceMin) : undefined);
  const pMax  = override.priceMax  ?? (pType === "List Price" && priceMax !== "Any" ? Number(priceMax) : undefined);

  return {
    listingType: lt,
    propertyType: ptCsv,
    bedrooms: bmin,
    bathrooms: baMin,
    priceType: pType,
    priceMin: pMin,
    priceMax: pMax,
    // 🔽 only attach when Sold is selected
    soldSort: override.soldSort ?? (lt === "Sold" ? soldSort : undefined),
   soldWithinDays:
     override.soldWithinDays !== undefined
       ? override.soldWithinDays
       : (lt === "Sold" && isNum(soldWithinDays) ? soldWithinDays : undefined),
  };  
};
const doSearch = (override = {}) => {
  // Merge current UI state with any one-off overrides (incl. soldSort when Sold)
  const effective = getEffective(override); // { listingType, propertyType, bedrooms, bathrooms, priceType, priceMin, priceMax, soldSort? }

  // 1) ZIP override explicitly provided (e.g., from suggestions)
  if (override.zipCode) {
    onSearch({
      ...effective,
      zipCode: override.zipCode,
      // force text search params off when using ZIP
      city: undefined,
      state: undefined,
      location: undefined,
    });
    (async () => {
      try {
        onBoundary?.(await fetchZipGeoJSON(override.zipCode));
      } catch {
        onBoundary?.(null);
      }
    })();
    return;
  }

  // 2) Explicit location (city/state or "X County, ST")
  if (override.location) {
    onSearch({
      ...effective,
      location: override.location,
      city: override.city,
      state: override.state,
      // ensure we’re not mixing with a ZIP in downstream handlers
      zipCode: undefined,
    });
    (async () => {
      try {
        if (/county/i.test(override.location) && override.state) {
          const countyName = override.location
            .replace(/,?\s*[A-Z]{2}\s*$/, "")
            .replace(/county/i, "")
            .trim();
          onBoundary?.(await fetchCountyGeoJSON(countyName, override.state));
        } else if (override.city && override.state) {
          onBoundary?.(await fetchPlaceGeoJSON(override.city, override.state));
        } else {
          onBoundary?.(null);
        }
      } catch {
        onBoundary?.(null);
      }
    })();
    return;
  }

  // 3) If raw input currently is a ZIP (user typed 5 digits and hit enter)
  const txt = input.trim();
  if (/^\d{5}$/.test(txt)) {
    onSearch({
      ...effective,
      zipCode: txt,
      city: undefined,
      state: undefined,
      location: undefined,
    });
    (async () => {
      try {
        onBoundary?.(await fetchZipGeoJSON(txt));
      } catch {
        onBoundary?.(null);
      }
    })();
    return;
  }

  // 4) If we already captured a city/state from a suggestion click
  if (selectedCity && selectedState) {
    onSearch({
      ...effective,
      city: selectedCity,
      state: selectedState,
      location: `${selectedCity}, ${selectedState}`,
      zipCode: undefined,
    });
    (async () => {
      try {
        onBoundary?.(await fetchPlaceGeoJSON(selectedCity, selectedState));
      } catch {
        onBoundary?.(null);
      }
    })();
    return;
  }

  // 5) Fallback (no explicit location/zip)
  //    ✔️ Preserve the last known location (don’t clear it).
  onSearch({
    ...effective,
  });
  onBoundary?.(null);
};

  // each filter “wrapper” sets state *and* immediately fires doSearch with the new value
const handleListingTypeChange  = (val) => {
  setListingType(val);
  doSearch({ listingType: val });          // override wins in getEffective()
};

const handlePropertyTypeChange = (vals) => {
  setPropertyType(vals);
  doSearch({ propertyType: vals.join(",") });
};

const handleBedBathChange = (val) => {
  setBedBath(val);
  doSearch({
    bedrooms:  val.bedrooms  !== "Any" ? Number(val.bedrooms.replace("+",""))   : undefined,
    bathrooms: val.bathrooms !== "Any" ? Number(val.bathrooms.replace("+",""))  : undefined,
  });
};

const handlePriceTypeChange = (val) => {
  setPriceType(val);
  doSearch({ priceType: val });
};

// your PriceRangeDropdown is good — it passes explicit overrides:
// your PriceRangeDropdown is good — it passes explicit overrides:

  // on first mount run one search
  useEffect(() => {
    if (didMount.current) return;
    didMount.current = true;
    doSearch();
  }, []);

  const handleInputChange = (e) => {
    const v = e.target.value;
    setInput(v);
    if (v.length < 3 || !window.google?.maps?.places) {
      setSuggestions([]);
      return;
    }
    new window.google.maps.places.AutocompleteService().getPlacePredictions(
      { input: v, types: ["(regions)"], componentRestrictions: { country: "us" } },
      (preds, status) => {
        setSuggestions(status === "OK" ? preds.map(p => p.description) : []);
      }
    );
  };

// Sources:
// ZIP (ZCTA):   USA_ZIP_Code_Tabulation_Areas
// County:       USA_Counties
// Place/City:   USA_Places
// ---------- ArcGIS helpers (single copy only) ----------
const ESRI = {
  zcta:   "https://services.arcgis.com/P3ePLMYs2RVChkJx/ArcGIS/rest/services/USA_ZIP_Code_Tabulation_Areas/FeatureServer/0/query",
  county: "https://services.arcgis.com/P3ePLMYs2RVChkJx/ArcGIS/rest/services/USA_Counties/FeatureServer/0/query",
  place:  "https://services.arcgis.com/P3ePLMYs2RVChkJx/ArcGIS/rest/services/USA_Places/FeatureServer/0/query",
};


function arcgisToGeoJSONFeatureCollection(arc) {
  if (!arc || !Array.isArray(arc.features)) return { type: "FeatureCollection", features: [] };

  const features = arc.features.map(f => {
    const g = f.geometry || {};
    let geometry = null;

    if (Array.isArray(g.rings)) {
      // Polygon or MultiPolygon
      // ArcGIS rings are arrays of linear rings. If there are disjoint shells, treat as MultiPolygon.
      const polys = splitRingsIntoPolygons(g.rings);
      geometry = polys.length > 1
        ? { type: "MultiPolygon", coordinates: polys.map(rings => rings.map(r => r.map(([x,y]) => [x, y]))) }
        : { type: "Polygon", coordinates: polys[0].map(r => r.map(([x,y]) => [x, y])) };
    } else if (Array.isArray(g.paths)) {
      // LineString/MultiLineString
      geometry = g.paths.length > 1
        ? { type: "MultiLineString", coordinates: g.paths.map(p => p.map(([x,y]) => [x, y])) }
        : { type: "LineString", coordinates: g.paths[0].map(([x,y]) => [x, y]) };
    } else if (typeof g.x === "number" && typeof g.y === "number") {
      // Point
      geometry = { type: "Point", coordinates: [g.x, g.y] };
    }

    return {
      type: "Feature",
      geometry,
      properties: f.attributes || {}
    };
  }).filter(ft => ft.geometry);

  return { type: "FeatureCollection", features };
}

// Heuristic: group rings into polygons by shell/holes orientation
function splitRingsIntoPolygons(rings) {
  // ArcGIS uses clockwise outer rings, counter-clockwise holes (most of the time).
  const outer = [];
  const holes = [];

  for (const ring of rings) {
    if (ring.length < 4) continue;
    (ringArea(ring) < 0 ? outer : holes).push(ring);
  }

  // assign holes to the nearest outer they fall within (simple bbox check fallback)
  const polys = outer.map(r => [r]);
  for (const h of holes) {
    let placed = false;
    for (const poly of polys) {
      if (ringWithin(h, poly[0])) { poly.push(h); placed = true; break; }
    }
    if (!placed) polys.push([h]); // safety: treat as its own polygon if we can't place
  }
  return polys;
}
// Put this helper near the top of ZillowSearchBar.jsx (or in your UI helpers)
function SelectDropdown({ value, onChange, children, title }) {
  return (
    <div style={{ display: "inline-block" }}>
      <select
        value={value}
        onChange={onChange}
        title={title}
        style={{
          padding: 8,
          fontSize: 14,
          border: "1px solid #ccc",
          borderRadius: 4,
          background: "#fff",
          cursor: "pointer",
          minWidth: 180,
          boxSizing: "border-box",
        }}
      >
        {children}
      </select>
    </div>
  );
} 

function ringArea(ring){
  // signed area; negative ≈ clockwise in ESRI 4326
  let s = 0;
  for (let i=0, j=ring.length-1; i<ring.length; j=i++) {
    const [x1,y1] = ring[j], [x2,y2] = ring[i];
    s += (x2 - x1) * (y2 + y1);
  }
  return s;
}

function ringWithin(hole, shell){
  // quick bbox containment; good enough for our purpose
  const hb = bbox(hole), sb = bbox(shell);
  return hb.minX >= sb.minX && hb.maxX <= sb.maxX && hb.minY >= sb.minY && hb.maxY <= sb.maxY;
}
function bbox(r){
  let minX=Infinity, minY=Infinity, maxX=-Infinity, maxY=-Infinity;
  for (const [x,y] of r){ if(x<minX)minX=x; if(x>maxX)maxX=x; if(y<minY)minY=y; if(y>maxY)maxY=y; }
  return {minX,minY,maxX,maxY};
}
// Single fetch with timeout  ✅ must come first
const fetchWithTimeout = (url, ms = 9000) =>
  Promise.race([
    fetch(url),
    new Promise((_, rej) => setTimeout(() => rej(new Error("timeout")), ms)),
  ]);


// ---------- ArcGIS providers (public) ----------
const PROVIDERS = {
  zcta: [

   { url: "https://services1.arcgis.com/5T5nSi527N4F7luB/arcgis/rest/services/USA_ZIP_Code_Areas/FeatureServer/0/query", fields: ["ZIP","GEOID","NAME","ZCTA5CE10","ZCTA5CE20"] },
   { url: "https://services.arcgis.com/P3ePLMYs2RVChkJx/ArcGIS/rest/services/USA_ZIP_Code_Tabulation_Areas/FeatureServer/0/query", fields: ["ZCTA5CE20","ZCTA5CE10","GEOID20","GEOID10","NAME"] },
  ],
  place: [
   { url: "https://services.arcgis.com/P3ePLMYs2RVChkJx/ArcGIS/rest/services/USA_Places/FeatureServer/0/query", fields: ["STATE_ABBR","ST","STATE"] },
  ],
  county: [
   { url: "https://services.arcgis.com/P3ePLMYs2RVChkJx/ArcGIS/rest/services/USA_Counties/FeatureServer/0/query", fields: ["STATE_ABBR","ST","STATE"] },
  ],
};

// Single fetch with timeout

// ArcGIS JSON → GeoJSON (minimal, same as I sent before)
function arcgisToGeoJSONFeatureCollection(arc) {
  if (!arc || !Array.isArray(arc.features)) return { type: "FeatureCollection", features: [] };

  const features = arc.features.map(f => {
    const g = f.geometry || {};
    let geometry = null;

    if (Array.isArray(g.rings)) {
      const polys = splitRingsIntoPolygons(g.rings);
      geometry = polys.length > 1
        ? { type: "MultiPolygon", coordinates: polys.map(rings => rings.map(r => r.map(([x,y]) => [x, y]))) }
        : { type: "Polygon", coordinates: polys[0].map(r => r.map(([x,y]) => [x, y])) };
    } else if (Array.isArray(g.paths)) {
      geometry = g.paths.length > 1
        ? { type: "MultiLineString", coordinates: g.paths.map(p => p.map(([x,y]) => [x, y])) }
        : { type: "LineString", coordinates: g.paths[0].map(([x,y]) => [x, y]) };
    } else if (typeof g.x === "number" && typeof g.y === "number") {
      geometry = { type: "Point", coordinates: [g.x, g.y] };
    }

    return { type: "Feature", geometry, properties: f.attributes || {} };
  }).filter(ft => ft.geometry);

  return { type: "FeatureCollection", features };
}
function splitRingsIntoPolygons(rings) {
  const outer = [], holes = [];
  for (const ring of rings) {
    if (ring.length < 4) continue;
    (ringArea(ring) < 0 ? outer : holes).push(ring);
  }
  const polys = outer.map(r => [r]);
  for (const h of holes) {
    let placed = false;
    for (const poly of polys) {
      if (ringWithin(h, poly[0])) { poly.push(h); placed = true; break; }
    }
    if (!placed) polys.push([h]);
  }
  return polys;
}
function ringArea(r){ let s=0; for (let i=0,j=r.length-1;i<r.length;j=i++) { const [x1,y1]=r[j],[x2,y2]=r[i]; s += (x2-x1)*(y2+y1); } return s; }
function ringWithin(h,s){ const hb=bbox(h), sb=bbox(s); return hb.minX>=sb.minX && hb.maxX<=sb.maxX && hb.minY>=sb.minY && hb.maxY<=sb.maxY; }
function bbox(r){ let minX=Infinity,minY=Infinity,maxX=-Infinity,maxY=-Infinity; for (const [x,y] of r){ if(x<minX)minX=x; if(x>maxX)maxX=x; if(y<minY)minY=y; if(y>maxY)maxY=y; } return {minX,minY,maxX,maxY}; }

const sqlQuote = (s="") => String(s).replace(/'/g,"''");

// Generic: try many providers + field combos
async function tryQueryAcrossProviders(kind, buildWhereList) {
  const providers = PROVIDERS[kind] || [];
  for (const prov of providers) {
    const base = prov.url;
    const whereList = buildWhereList(prov.fields || []);
    for (const where of whereList) {
      const u = new URL(base);
      u.searchParams.set("where", where);
      u.searchParams.set("outFields", "*");
      u.searchParams.set("outSR", "4326");
      u.searchParams.set("returnGeometry", "true");
      u.searchParams.set("f", "json");

      try {
        const res = await fetchWithTimeout(u.toString(), 9000);
        if (!res.ok) continue;
        const arc = await res.json();
        const gj = arcgisToGeoJSONFeatureCollection(arc);
        if (gj?.features?.length) return gj;
      } catch { /* try next */ }
    }
  }
  throw new Error("No geometry (all providers/fields failed)");
}




// --- ZIP polygon helpers (fetch ZCTA + point-in-polygon) ---


// ── ZIP polygon + geometry helpers ─────────────────────────────────────────────

// Public ZCTA sources (try first that works)


// Convert GeoJSON → array of polygons (each polygon: [outerRing, hole1, ...])
function polysFromGeoJSON(geojson) {
  const feats = geojson?.type === 'FeatureCollection'
    ? geojson.features
    : geojson?.type === 'Feature'
      ? [geojson]
      : geojson
        ? [{ type: 'Feature', geometry: geojson }]
        : [];

  const out = [];
  feats.forEach(f => {
    const g = f.geometry;
    if (!g) return;
    if (g.type === 'Polygon') {
      out.push(
        g.coordinates.map(ring =>
          ring.map(([lng, lat]) => ({ latitude: lat, longitude: lng }))
        )
      );
    } else if (g.type === 'MultiPolygon') {
      g.coordinates.forEach(poly => {
        out.push(
          poly.map(ring =>
            ring.map(([lng, lat]) => ({ latitude: lat, longitude: lng }))
          )
        );
      });
    }
  });
  return out;
}

// Ray-casting PIP with holes
function pointInRing(pt, ring) {
  let inside = false;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const xi = ring[i].longitude, yi = ring[i].latitude;
    const xj = ring[j].longitude, yj = ring[j].latitude;
    const intersects = ((yi > pt.latitude) !== (yj > pt.latitude)) &&
      (pt.longitude < (xj - xi) * (pt.latitude - yi) / ((yj - yi) || 1e-12) + xi);
    if (intersects) inside = !inside;
  }
  return inside;
}
function pointInPolyWithHoles(pt, poly) {
  if (!poly?.length) return false;
  if (!pointInRing(pt, poly[0])) return false; // outside outer ring
  for (let h = 1; h < poly.length; h++) if (pointInRing(pt, poly[h])) return false; // inside a hole
  return true;
}
function pointInAnyPoly(pt, polys) {
  for (const poly of polys) if (pointInPolyWithHoles(pt, poly)) return true;
  return false;
}

// Fit a GeoJSON boundary on Leaflet and offset center for the right sidebar
function fitGeoJSONOnLeaflet(geojson, map, offsetX = 280) {
  try {
    if (!geojson || !map) return;
    const layer = L.geoJSON(geojson);
    const b = layer.getBounds();
    if (!b.isValid()) return;
    const c = b.getCenter();
    const z = map.getZoom();
    const pt = map.project(L.latLng(c.lat, c.lng), z);
    const shifted = L.point(pt.x - offsetX, pt.y);
    map.setView(map.unproject(shifted, z), z, { animate: true });
    map.fitBounds(b, { padding: [20, 20] });
  } catch {}
}




function bboxFromPolys(polys) {
  let minLat =  90, minLng =  180, maxLat = -90, maxLng = -180;
  polys.flat().flat().forEach(({ latitude: lat, longitude: lng }) => {
    if (lat < minLat) minLat = lat; if (lat > maxLat) maxLat = lat;
    if (lng < minLng) minLng = lng; if (lng > maxLng) maxLng = lng;
  });
  return { minLat, minLng, maxLat, maxLng };
}

// --- point-in-polygon with holes (ray casting) ---
function pointInRing(pt, ring) {
  let inside = false;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const xi = ring[i].longitude, yi = ring[i].latitude;
    const xj = ring[j].longitude, yj = ring[j].latitude;
    const intersects = ((yi > pt.latitude) !== (yj > pt.latitude)) &&
      (pt.longitude < (xj - xi) * (pt.latitude - yi) / ((yj - yi) || 1e-12) + xi);
    if (intersects) inside = !inside;
  }
  return inside;
}
function pointInPolyWithHoles(pt, poly) {
  if (!poly?.length) return false;
  if (!pointInRing(pt, poly[0])) return false;        // outer
  for (let h = 1; h < poly.length; h++) {             // holes
    if (pointInRing(pt, poly[h])) return false;
  }
  return true;
}


// Leaflet fitting helper (fits the polygon nicely)

async function fetchPlaceGeoJSON(cityName, stateAbbr) {
  const c = sqlQuote(cityName).toUpperCase();
  const s = sqlQuote(stateAbbr).toUpperCase();
  return tryQueryAcrossProviders("place", (stateFields) => {
    const wheres = [];
    for (const sf of stateFields) wheres.push(`UPPER(NAME)='${c}' AND ${sf}='${s}'`);
    wheres.push(`UPPER(NAME)='${c}'`); // fallback
    return wheres;
  });
}

async function fetchCountyGeoJSON(countyName, stateAbbr) {
  const c = sqlQuote(countyName.replace(/ county$/i, "")).toUpperCase();
  const s = sqlQuote(stateAbbr).toUpperCase();
  return tryQueryAcrossProviders("county", (stateFields) => {
    const wheres = [];
    for (const sf of stateFields) wheres.push(`UPPER(NAME)='${c}' AND ${sf}='${s}'`);
    wheres.push(`UPPER(NAME)='${c}'`);
    return wheres;
  });
}













const handleSelect = (description) => {
  // ZIP?
  const zipMatch = description.match(/\b(\d{5})\b/);
  if (zipMatch) {
    const zip = zipMatch[1];
    setSelectedCity(""); setSelectedState("");
    setInput(zip);
    setSuggestions([]);
    doSearch({ zipCode: zip });

    // 🔷 boundary for ZIP
    (async () => {


      try {
        const gj = await fetchZipGeoJSON(zip);
          console.log("ZIP features:", gj?.features?.length, gj);

        onBoundary?.(gj);
      } catch (e) {
  console.warn("ZIP boundary failed:", e);
  onBoundary?.(null);
}
    })();

    return;
  }

  // else geocode for city/state
  setInput(description);
  setSuggestions([]);
  new window.google.maps.Geocoder().geocode(
    { address: description },
    (results, status) => {
      if (status === "OK" && results[0]) {
        const comps = results[0].address_components;
        const city  = comps.find(c => c.types.includes("locality"))?.long_name;
        const county= comps.find(c => c.types.includes("administrative_area_level_2"))?.long_name; // "X County"
        const state = comps.find(c => c.types.includes("administrative_area_level_1"))?.short_name;
        const loc   = results[0].geometry?.location;
        const center= loc ? { lat: loc.lat(), lng: loc.lng() } : null;

        if (city && state) {
          setSelectedCity(city); setSelectedState(state);
          doSearch({ location: description, city, state, center });

          // 🔷 boundary for city (place)
          (async () => {
            try {
              const gj = await fetchPlaceGeoJSON(city, state);
              onBoundary?.(gj);
            } catch {
              // If the user typed "... County, ST", try county instead
              if (/county/i.test(description) && state) {
                try {
                  const countyName = description.replace(/,?\s*[A-Z]{2}\s*$/,'').replace(/county/i,'').trim();
                  const gj2 = await fetchCountyGeoJSON(countyName, state);
                  onBoundary?.(gj2);
                } catch { onBoundary?.(null); }
              } else {
                onBoundary?.(null);
              }
            }
          })();

        } else {
          doSearch({ location: description, city: "", state: "", center });
          onBoundary?.(null);
        }
      } else {
        doSearch({ location: description, city: "", state: "" });
        onBoundary?.(null);
      }
    }
  );
};

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      doSearch();
    }
  };

  return (
<div style={{ display:'flex', flexWrap:'wrap', alignItems:'center', gap:8, background:'transparent', padding:0, borderRadius:0, width:'100%', boxSizing:'border-box' }}>
      {/* Location/ZIP */}
      <div style={{ flex: 2, minWidth: 200 }}>
        {/* wrapper for input + suggestions */}
        <div style={{ position: "relative", marginTop: 20 }}>
          <input
            type="text"
            placeholder="City, State or ZIP"
            value={input}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            style={{
              width: "100%",
              padding: 8,
              fontSize: 14,
              border: "1px solid #ccc",
              borderRadius: 4,
              boxSizing: "border-box"
            }}
          />
           <button
           type="button"
           title="Use my current location"
           aria-label="Use my current location"
           onMouseDown={(e) => e.preventDefault()} // prevent input blur while clicking
           onClick={() => {
  if (typeof onNearMe === 'function') {
    onNearMe();
    return;
  }
  if (!("geolocation" in navigator)) {
   console.warn("Geolocation API not available (needs https or localhost).");
    return;
  }
  navigator.geolocation.getCurrentPosition(
    (pos) => {
      const { latitude, longitude } = pos.coords || {};
      if (latitude && longitude) {
        setInput("Near me");
        setSuggestions([]);
        onSearch?.({
          listingType,
          propertyType: propertyType.join(","),
          bedrooms: bedBath.bedrooms !== "Any" ? Number(bedBath.bedrooms.replace("+","")) : undefined,
          bathrooms: bedBath.bathrooms !== "Any" ? Number(bedBath.bathrooms.replace("+","")) : undefined,
          priceType,
          priceMin: priceType === "List Price" && priceMin !== "Any" ? Number(priceMin) : undefined,
          priceMax: priceType === "List Price" && priceMax !== "Any" ? Number(priceMax) : undefined,
          latitude,
          longitude,
        });
      }
    },
   (err) => {
     console.warn("Geolocation failed:", err?.message || err);
     alert("Couldn't get your location. Check browser permissions and that you're on HTTPS or localhost.");
  },
    { enableHighAccuracy: false, maximumAge: 10000, timeout: 8000 }
  );
}}
           style={{
             position: "absolute",
             right: 8,
             top: "50%",
             transform: "translateY(-50%)",
             background: "transparent",
             border: "none",
             padding: 4,
             cursor: "pointer",
             lineHeight: 0,
             display: "inline-flex",
             alignItems: "center",
             justifyContent: "center",
           }}
         >
           {/* Simple paper-airplane SVG */}
           <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#0074e4" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
             <path d="M22 2L11 13" />
             <path d="M22 2L15 22l-4-9-9-4 20-7z" />
           </svg>
         </button>
          {suggestions.length > 0 && (
            <ul style={{
              position: "absolute",
              top: "100%",
              left: 0,
              right: 0,
              margin: 0,
              padding: 0,
              listStyle: "none",
              background: "#fff",
              boxShadow: "0 2px 6px rgba(0,0,0,0.15)",
              zIndex: 100
            }}>
              {suggestions.map(s => (
                <li
                  key={s}
                  onMouseDown={e => { e.preventDefault(); handleSelect(s); }}
                  style={{ padding: "6px 8px", cursor: "pointer", fontSize: 14 }}
                >
                  {s}
                </li>
              ))}
            </ul>
          )}
        </div>
        {/* clickable hint below input */}
        <div style={{ marginTop: 4, fontSize: 12, color: "#555" }}>
          or{" "}
          <span
            onClick={() => setShowExactModal(true)}
            style={{ cursor: "pointer", color: "#0074e4", textDecoration: "underline" }}
          >
            Search Exact Address
          </span>
        </div>
      </div>
  
      {/* Price */}
      <div style={{ flex: "0 0 auto" }}>
<PriceRangeDropdown
  minBound={0}
  maxBound={1000000}
  step={5000}
  value={[
    Number.isFinite(Number(priceMin)) ? Number(priceMin) : 0,
    Number.isFinite(Number(priceMax)) && Number(priceMax) > 0 ? Number(priceMax) : 1000000,
  ]}
  onApply={(range) => {
    const [min, max] = range;          // avoid top-level destructuring in the prop if you prefer
    setPriceType("List Price");
    setPriceMin(min);
    setPriceMax(max);
    doSearch({ priceType: "List Price", priceMin: min, priceMax: max });
  }}
/>
      </div>
  
      {/* Listing Type */}
      <div style={{ flex: "0 0 auto" }}>
        <RadioDropdown
          value={listingType}
          onChange={handleListingTypeChange}
          options={[
            { value: "For Sale", label: "For Sale" },
            { value: "For Rent", label: "For Rent" },
            { value: "Sold",     label: "Sold"     },
          ]}
        />
      </div>
      {/* Sold sort (only show when Sold is selected) */}

{listingType === "Sold" && (
  <>
    {/* Sold sort */}
    {/* <div style={{ flex: "0 0 auto" }}>
      <SelectDropdown
        value={soldSort}
        onChange={(e) => {
          const v = e.target.value;
          setSoldSort(v);
          doSearch({ soldSort: v });
        }}
        title="Sort sold results"
      >
        <option value="sold_date">Most recently sold</option>
        <option value="lowest_price">Lowest list price</option>
        <option value="lot_size">Largest lot</option>
        <option value="photo_count">Most photos</option>
      </SelectDropdown>
    </div> */}

    {/* Sold timeframe */}
    <div style={{ flex: "0 0 auto" }}>
      <SelectDropdown
        value={String(soldWithinDays ?? "")}
        onChange={(e) => {
          const v = e.target.value;
          const days = v === "" ? null : Number(v);
          setSoldWithinDays(days);
          doSearch({ soldWithinDays: days });
        }}
        title="Sold in the last..."
      >
        <option value="">Any time</option>
        <option value="1">Last 24 hours</option>
        <option value="7">Last 7 days</option>
        <option value="30">Last 30 days</option>
        <option value="180">Last 6 months</option>
        <option value="365">Last 12 months</option>
      </SelectDropdown>
    </div>
  </>
)}
  
      {/* Property Type */}
      <div style={{ flex: "0 0 auto" }}>
        <CheckboxDropdown
          value={propertyType}
          onChange={handlePropertyTypeChange}
          options={[
            { value: "Houses",       label: "Houses" },
            { value: "Townhomes",    label: "Townhomes" },
            { value: "Multi-Family", label: "Multi-Family" },
            { value: "Condos/Co-Ops",label: "Condos/Co-Ops" },
            { value: "Lots/Land",    label: "Lots/Land" },
            { value: "Apartments",   label: "Apartments" },
            { value: "Manufactured", label: "Manufactured" },
          ]}
        />
      </div>
  
      {/* Beds & Baths */}
      <div style={{ flex: "0 0 auto" }}>
        <BedroomsBathroomsDropdown
          value={bedBath}
          onChange={handleBedBathChange}
        />
      </div>
  
      {showExactModal && (
        <ExactAddressModal
          onClose={() => setShowExactModal(false)}
          onSelect={async address => {
            try {
              const { data } = await axios.get(
                'https://zillow-com1.p.rapidapi.com/property',
                {
                  params: { address },
                  headers: {
                    'x-rapidapi-host': 'zillow-com1.p.rapidapi.com',
                    'x-rapidapi-key': '510e42edf9msh1028fbc335ec8e9p12456ejsn237f189ede04'
                  }
                }
              );
              onSelectExactListing(data);
            } catch (err) {
              console.error('Exact address lookup failed', err);
            } finally {
              setShowExactModal(false);
            }
          }}
        />
      )}
    </div>
  );
  

  
};

const ZillowClonePage = () => {
const DEFAULT_CENTER = [35.1495, -90.0490]; // Memphis

  const { currentUser } = useAuth();
  const [markerLabel, setMarkerLabel] = useState('price'); // ← NEW
  const [exactListing, setExactListing] = useState(null)
 const [geoStatus, setGeoStatus] = useState('idle'); // 'idle' | 'success' | 'error'
 const [mapStyle, setMapStyle]   = useState('classic');  // Map center
const [mapCenter, setMapCenter] = useState(DEFAULT_CENTER);// // Favorites
const [sellerFinOnly, setSellerFinOnly] = useState(false);
 const [leafletMap, setLeafletMap] = useState(null);
  // Data states
  const [onMarketListings, setOnMarketListings] = useState([]);
  const [listings, setListings]                 = useState([]);
  const [isSearchTriggered, setIsSearchTriggered] = useState(false);
  const [showOffMarket, setShowOffMarket]       = useState(false);
  const [loading, setLoading]                   = useState(true);
  const [boundaryGeoJSON, setBoundaryGeoJSON] = useState(null);

// Follow user (near-me) & params for searching
const [followUser, setFollowUser] = useState(true);
const [userLocation, setUserLocation] = useState(null);
const [searchParams, setSearchParams] = useState({
  location: "",              // text search (city, "City, ST", ZIP). ZIP → radius 0
  sort: "relevant",
  type: [],                  // ["single_family","condos",...]
  "beds-min": 0,
  "beds-max": 0,
  "baths-min": 0,
  "baths-max": 0,
  "list_price-min": 0,
  "list_price-max": 0,
  "sqft-min": 0,
  "sqft-max": 0,
  tags: [],
  keywords: "",
  // coord search:
  latitude: null,
  longitude: null,
  limit: 200,
  search_radius: 30,
  page: "1",
});
  // Modal & selection
  const [selectedListing, setSelectedListing]   = useState(null);
  const [listingOffMarketDetails, setListingOffMarketDetails] = useState(null);
  const [isModalOpen, setIsModalOpen]           = useState(false);
  const [isOffMarketModalOpen, setIsOffMarketModalOpen] = useState(false);
// at top of ZillowClonePage component
const [favorites, setFavorites] = useState([]);             // array<string> of favorite ids
const [showFavoritesOnly, setShowFavoritesOnly] = useState(false);
 const geoResolvedRef = useRef(false);
const lastQueryRef = useRef(null); // { city, state, zipCode, listingType, propertyType, ... }
const userHasPriceRef = useRef(false);
  // Memphis fallback immediately
  useEffect(() => {
    (async () => {
     try {
     if (geoResolvedRef.current) return; // already got current location
        await fetchListings({
          state: "TN",
          city:  "Memphis",
          listingType:  "For Sale",
          propertyType: "Single Family",
          priceType:    "List Price",
        });
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // Background geolocation
const daysSince = (d) => {
  if (!d) return Infinity;
  const n = dayjs();
  const t = dayjs(d);
  return t.isValid() ? n.diff(t, "day") : Infinity;
};

// Try many possible places a "sold date" could live in your mapped listing.
const pickSoldDate = (l = {}) => {
  const fromPriceHistory = (() => {
    const ph = l.priceHistory || l.history || l.events;
    if (!Array.isArray(ph)) return null;
    const soldEvt = ph.find(e =>
      /sold/i.test(e?.type || e?.event || e?.status || "")
    );
    return soldEvt?.date || soldEvt?.recordDate || null;
  })();

  const unix = Number(l?.soldTimestamp);
  const fromUnix = Number.isFinite(unix) ? dayjs.unix(unix).toISOString() : null;

  const candidates = [
    l?.soldDate,
    l?.dateSold,
    l?.lastSoldDate,
    l?.sold_on,
    l?.sold_time,          // sometimes an ISO string
    fromUnix,
    fromPriceHistory,
  ].filter(Boolean);

  return candidates[0] || null;
};
const getDocId = (l) =>
  String(l?.id ?? l?.property_id ?? l?.listing_id ?? l?.zpid ?? "");
// If you use Firebase on web (either global or imported)
useEffect(() => {
  if (!currentUser?.uid) {
    // still honor local cache if signed out
    try {
      const raw = localStorage.getItem('favorites');
      if (raw) setFavorites(JSON.parse(raw).map(String));
    } catch {}
    return;
  }

  const fb = window.firebase || undefined;
  const fs = fb?.firestore ? fb.firestore() : undefined;

  // If FS is not available on web, just load from local cache
  if (!fs) {
    try {
      const raw = localStorage.getItem('favorites');
      if (raw) setFavorites(JSON.parse(raw).map(String));
    } catch {}
    return;
  }

  const unsub = fs
    .collection('users')
    .doc(String(currentUser.uid))
    .collection('favorites')
    .onSnapshot((snap) => {
      // merge FS ids with local cache so we never lose optimistic toggles
      const fsIds = snap.docs.map(d => String(d.id));

      let localIds = [];
      try {
        const raw = localStorage.getItem('favorites');
        if (raw) localIds = JSON.parse(raw).map(String);
      } catch {}

      const merged = Array.from(new Set([...localIds, ...fsIds]));
      setFavorites(merged);

      // keep local cache in sync with whatever we end up displaying
      try { localStorage.setItem('favorites', JSON.stringify(merged)); } catch {}
    });

  return () => unsub();
}, [currentUser?.uid]);
  // Adjust map center when listings change
useEffect(() => {
  // Only let listings recenter when we have NOT successfully geolocated yet
 if (geoStatus === 'error' && !userLocation && listings.length > 0) {
    const { latitude, longitude } = listings[0];
    const lat = Number(latitude), lng = Number(longitude);
   setMapCenter(isNaN(lat) || isNaN(lng) ? DEFAULT_CENTER : [lat, lng]);
  }
}, [listings, showOffMarket, userLocation, geoStatus]);
  // Infinite scroll observer


  // map your UI "Single Family" etc → API strings (assumes PROPERTY_TYPE_MAP exists)
const mapTypes = (csv) =>
  csv?.split(",")
     .map(label => PROPERTY_TYPE_MAP[label])
     .filter(Boolean)
     .join(",");

// COORDINATE SEARCH (near-me or explicit coords)
// COORDINATE SEARCH (near-me or explicit coords)
async function fetchByCoords({
  latitude,
  longitude,
  radius = 30,
  listingType = "For Sale",
  priceType,
  priceMin,
  priceMax,
  // pass this from handleSearch / doSearch when listingType === "Sold"
  soldWithinDays, // e.g., 1, 7, 30, 180, 365 or undefined/null for "any time"
}) {
  // tiny helper; uses dayjs already in your file
  const daysSince = (d) => {
    if (!d) return Infinity;
    const n = dayjs();
    const t = dayjs(d);
    return t.isValid() ? n.diff(t, "day") : Infinity;
  };

  try {
    // Determine the sort to use for Sold results (fallback to "sold_date")
    const sortParam =
      listingType === "Sold"
        ? (lastQueryRef.current?.soldSort ?? "sold_date")
        : undefined;

    // Persist latest query knobs for later merges — and 🔒 force coord mode
    lastQueryRef.current = {
      ...lastQueryRef.current,

      // clear any text-search anchors so we don't snap back to Memphis
      city: undefined,
      state: undefined,
      zipCode: undefined,
      location: undefined,

      latitude,
      longitude,
      search_radius: radius,

      listingType: listingType || "For Sale",
      propertyType: "",

      priceType,
      priceMin,
      priceMax,

      ...(listingType === "Sold" ? { soldSort: sortParam } : {}),
      ...(listingType === "Sold" && soldWithinDays != null
        ? { soldWithinDays: Number(soldWithinDays) }
        : {}),
    };

    const url = makeCoordUrl(listingType);

    // Build query for the coord endpoint
    const query = {
      latitude,
      longitude,
      radius,
      ...(sortParam ? { sort: sortParam } : {}),
    };

    const resp = await apiGet(url, query, API_HEADERS);

    setOnMarketListings([]);
    const raw = resp?.data?.properties || [];

    const mapped = raw
      .filter(
        (p) =>
          p?.location?.address?.coordinate?.lat != null &&
          p?.location?.address?.coordinate?.lon != null
      )
      .map((p) => mapNewPropertyToOld(p, { listingMode: listingType }));

    // Apply price filter locally (API may ignore price on coord endpoint)
    const lo = Number.isFinite(Number(priceMin)) ? Number(priceMin) : null;
    const hi = Number.isFinite(Number(priceMax)) ? Number(priceMax) : null;

    let filtered = (lo == null && hi == null)
      ? mapped
      : mapped.filter((l) => {
          const pr = Number(l?.price);
          if (!Number.isFinite(pr)) return false;
          if (lo != null && pr < lo) return false;
          if (hi != null && pr > hi) return false;
          return true;
        });

    // Sold timeframe filter (client-side, reliable)
  // Sold timeframe filter (client-side, resilient to different field names)
 if (listingType === "Sold") {
    const maxDaysRaw =
    isNum(soldWithinDays) ? soldWithinDays
    : isNum(lastQueryRef.current?.soldWithinDays) ? lastQueryRef.current.soldWithinDays
    : null;
  const maxDays = isNum(maxDaysRaw) && maxDaysRaw > 0 ? maxDaysRaw : null;

  if (maxDays != null) {
    const withDates = filtered.filter(l => !!pickSoldDate(l));
    if (withDates.length > 0) {
      filtered = withDates.filter(l => {
        const d = pickSoldDate(l);
        return d && daysSince(d) <= maxDays;
      });
    } else {
      // No parsable dates in this batch — don’t hide everything.
      console.warn("[Sold filter] No parsable sold dates from coord endpoint; skipping timeframe filter.");
    }
  }
 }

    // Seller-financing pass
    const stamped = filtered.map((l) => {
      if (l.sellerFinanced) return l;
      const d = detectSellerFinancing(l?.remarks || "");
      return d.isSellerFinanced
        ? {
            ...l,
            sellerFinanced: true,
            sellerFinScore: d.score,
            sellerFinMatches: d.matches,
          }
        : l;
    });

    setListings(stamped);
    setOnMarketListings(stamped);
    setIsSearchTriggered(true);
  } catch (err) {
    console.error(
      "coords fetch error:",
      err?.response?.status,
      err?.response?.data || err
    );
  }
}

  
// TEXT SEARCH ("City, ST" or general city)
async function fetchListings(params) {
  try {
    if (boundaryGeoJSON) setBoundaryGeoJSON(null);

    lastQueryRef.current = {
      city: params.city,
      state: params.state,
      listingType: params.listingType || "For Sale",
      propertyType: params.propertyType || "",
      priceType: params.priceType,
      priceMin: Number.isFinite(Number(params.priceMin)) ? Number(params.priceMin) : undefined,
      priceMax: Number.isFinite(Number(params.priceMax)) ? Number(params.priceMax) : undefined,
      soldSort: params?.soldSort ?? lastQueryRef.current?.soldSort, // keep the latest choice
        soldWithinDays: params?.soldWithinDays ?? lastQueryRef.current?.soldWithinDays,

    };

    const listingType = params.listingType || "For Sale";
    const types = mapTypes(params.propertyType);
    const base = makeBaseUrl(listingType);

    // ✅ compute sort once; no direct spread with `params` inlined
    const sortParam =
      listingType === "Sold"
        ? (params?.soldSort ?? lastQueryRef.current?.soldSort ?? "sold_date")
        : undefined;

    const raw = {
      location: `${params.city}, ${params.state}`,
      ...(types && { type: types }),
      ...(params.bedrooms  && { "beds-min": params.bedrooms }),
      ...(params.bathrooms && { "baths-min": params.bathrooms }),
      ...(params.priceType === "List Price" && Number.isFinite(Number(params.priceMin))
        ? { "list_price-min": Number(params.priceMin) }
        : {}),
      ...(params.priceType === "List Price" && Number.isFinite(Number(params.priceMax))
        ? { "list_price-max": Number(params.priceMax) }
        : {}),
      limit: 50,
      search_radius: 5,
      ...(sortParam ? { sort: sortParam } : {}),
    };

    console.log("🔍 API query:", raw);
    const resp = await apiGet(base, raw, API_HEADERS);
    const properties = resp?.data?.properties || [];

const mapped = properties
  .filter(
    (p) =>
      p?.location?.address?.coordinate?.lat != null &&
      p?.location?.address?.coordinate?.lon != null
  )
  .map((p) => mapNewPropertyToOld(p, { listingMode: listingType }));

// 🔽 NEW: Sold timeframe filter (client-side, reliable)
let finalList = mapped;

if (listingType === "Sold") {
  const maxDays =
    isNum(params?.soldWithinDays)
      ? params.soldWithinDays
      : isNum(lastQueryRef.current?.soldWithinDays)
        ? lastQueryRef.current.soldWithinDays
        : null;

  if (isNum(maxDays)) {
    // use the local maxDays, do NOT reference `soldWithinDays` here
    const withDates = mapped.filter(l => !!pickSoldDate(l));
    finalList = withDates.length
      ? withDates.filter(l => {
          const d = pickSoldDate(l);
          return d && daysSince(d) <= maxDays;
        })
      : mapped; // safety: if we can't detect dates, don't hide everything
  }
}

setListings(finalList);
setOnMarketListings(finalList);
setIsSearchTriggered(true);

    // ⬇️ recenter to first hit if present
    if (mapped.length) {
      const { latitude, longitude } = mapped[0];
      const lat = Number(latitude),
        lng = Number(longitude);
      if (Number.isFinite(lat) && Number.isFinite(lng)) {
        setMapCenter([lat, lng]);
        if (leafletMap) centerWithOffset(leafletMap, { lat, lng }, { x: 280, y: 0 });
      }
    }
  } catch (err) {
    console.error(`Error fetching ${params.listingType} listings:`, err);
  }
}

// TEXT SEARCH ("City, ST" or general city)
// ZIP SEARCH (ZIP → exact match, radius 0)
async function fetchListingsByZip(params) {
  try {
    if (boundaryGeoJSON) setBoundaryGeoJSON(null);

    lastQueryRef.current = {
      zipCode: params.zipCode,
      listingType: params.listingType || "For Sale",
      propertyType: params.propertyType || "",
      priceType: params.priceType,
      priceMin: Number.isFinite(Number(params.priceMin)) ? Number(params.priceMin) : undefined,
      priceMax: Number.isFinite(Number(params.priceMax)) ? Number(params.priceMax) : undefined,
      soldSort: params?.soldSort ?? lastQueryRef.current?.soldSort, // keep the latest choice
        soldWithinDays: params?.soldWithinDays ?? lastQueryRef.current?.soldWithinDays,

    };

    const listingType = params.listingType || "For Sale";
    const types = mapTypes(params.propertyType);
    const base = makeBaseUrl(listingType);

    // ✅ compute sort once here too
    const sortParam =
      listingType === "Sold"
        ? (params?.soldSort ?? lastQueryRef.current?.soldSort ?? "sold_date")
        : undefined;

    const raw = {
      location: params.zipCode,
      ...(types && { type: types }),
      ...(params.bedrooms  && { "beds-min": params.bedrooms }),
      ...(params.bathrooms && { "baths-min": params.bathrooms }),
      ...(params.priceType === "List Price" && Number.isFinite(Number(params.priceMin))
        ? { "list_price-min": Number(params.priceMin) }
        : {}),
      ...(params.priceType === "List Price" && Number.isFinite(Number(params.priceMax))
        ? { "list_price-max": Number(params.priceMax) }
        : {}),
      limit: 50,
      search_radius: 0,
      ...(sortParam ? { sort: sortParam } : {}),
    };

    const resp = await apiGet(base, raw, API_HEADERS);
    const properties = resp?.data?.properties || [];
const mapped = properties
  .filter(p => p?.location?.address?.coordinate?.lat != null && p?.location?.address?.coordinate?.lon != null)
  .map(p => mapNewPropertyToOld(p, { listingMode: listingType }));

let finalList = mapped;

if (listingType === "Sold") {
  const maxDays =
    isNum(params?.soldWithinDays)
      ? params.soldWithinDays
      : isNum(lastQueryRef.current?.soldWithinDays)
        ? lastQueryRef.current.soldWithinDays
        : null;

  if (isNum(maxDays)) {
    const withDates = mapped.filter(l => !!pickSoldDate(l));
    finalList = withDates.length
      ? withDates.filter(l => {
          const d = pickSoldDate(l);
          return d && daysSince(d) <= maxDays;
        })
      : mapped;
  }
}

setListings(finalList);
setOnMarketListings(finalList);
    setIsSearchTriggered(true);

    // ZIP polygon (ZCTA)
    try {
      const gj = await fetchZipGeoJSON(params.zipCode);
      setBoundaryGeoJSON(gj);
      fitGeoJSONOnLeaflet(gj, leafletMap, 280);
    } catch (e) {
      console.warn("ZIP boundary fetch failed:", e?.message || e);
      setBoundaryGeoJSON(null); // no mask if we couldn't load it
    }

    // map center fallback (first listing) if no boundary
    if (!boundaryGeoJSON && mapped.length) {
      const { latitude, longitude } = mapped[0] || {};
      const lat = Number(latitude),
        lng = Number(longitude);
      if (Number.isFinite(lat) && Number.isFinite(lng)) {
        setMapCenter([lat, lng]);
        if (leafletMap) centerWithOffset(leafletMap, { lat, lng }, { x: 280, y: 0 });
      }
    }
  } catch (err) {
    console.error(`❌ Error fetching ${params.listingType} by ZIP:`, err);
  }
}
 // Attempt geolocation once on mount; mark geoReady when done (success or error)
useEffect(() => {
  if (!followUser) { setGeoStatus('error'); return; }
  if (!('geolocation' in navigator)) { setGeoStatus('error'); return; }

  navigator.geolocation.getCurrentPosition(
  async (pos) => {
    if (geoResolvedRef.current) return;
    geoResolvedRef.current = true;

    const { latitude, longitude } = pos.coords || {};
    if (!latitude || !longitude) { setGeoStatus('error'); return; }

    // ✅ If user already ran a search with a price, don't overwrite it
   if (userHasPriceRef.current) {

      setGeoStatus('success');
      setLoading(false);
      return;
    }

    setUserLocation({ latitude, longitude });
    setSearchParams(p => ({ ...p, latitude, longitude, search_radius: 30, location: "", page: "1" }));

    setMapCenter([latitude, longitude]);
    if (leafletMap) centerWithOffset(leafletMap, { lat: latitude, lng: longitude }, { x: 280, y: 0 });

    try {
      await fetchByCoords({
        latitude,
        longitude,
        radius: 30,
        listingType: "For Sale",
        priceType: lastQueryRef.current?.priceType,
        priceMin:  lastQueryRef.current?.priceMin,
        priceMax:  lastQueryRef.current?.priceMax,
      });
      setGeoStatus('success');
    } catch {
      setGeoStatus('error');
    } finally {
      setLoading(false);
    }
  },

  
    async () => {
     if (geoResolvedRef.current) return;
     geoResolvedRef.current = true;

      // user denied / error → fallback to Memphis
      try {
        await fetchListings({
          state: "TN",
          city: "Memphis",
          listingType: "For Sale",
          propertyType: "Single Family",
          priceType: "List Price",
        });
      } finally {
        setLoading(false);
        setGeoStatus('error');
      }
    },
    { enableHighAccuracy: false, maximumAge: 10000, timeout: 8000 }
  );
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, []);

  
// --- tiny fetch with timeout

// Convert whatever comes back to a FeatureCollection

// Sources:
// ZIP (ZCTA):   USA_ZIP_Code_Tabulation_Areas
// County:       USA_Counties
// Place/City:   USA_Places

// Public entry points:

const handleSearch = (params) => {
  console.log("⚡️ handleSearch got", params);
 setListings([]); // keep this if you want the quick clear; just don't flip isSearchTriggered here


  if (params?.center && Number.isFinite(params.center.lat) && Number.isFinite(params.center.lng)) {
    setMapCenter([params.center.lat, params.center.lng]);
    if (leafletMap) centerWithOffset(leafletMap, params.center, { x: 280, y: 0 });
  }

  // Merge with last
const prune = (obj = {}) =>
  Object.fromEntries(Object.entries(obj).filter(([, v]) => v !== undefined));

const merged = { ...(lastQueryRef.current || {}), ...prune(params || {}) };
  // ✅ IMPORTANT: write the latest filters immediately
  lastQueryRef.current = merged;

 // ✅ remember if the user has set a price range
 if (
   merged?.priceType === "List Price" &&
   (merged?.priceMin != null || merged?.priceMax != null)
 ) {
   userHasPriceRef.current = true;
 }
  if (merged.zipCode) {
  // ZIP takes priority
  fetchListingsByZip(merged);

} else if (Number.isFinite(merged.latitude) && Number.isFinite(merged.longitude)) {
  // ✅ Prefer coord mode if coords exist in the merged query (e.g., from Near Me)
  fetchByCoords({
    latitude: merged.latitude,
    longitude: merged.longitude,
    radius: merged.search_radius ?? 30,
    listingType: merged.listingType || "For Sale",
    priceType: merged.priceType,
    priceMin: isNum(merged.priceMin) ? merged.priceMin : undefined,
   priceMax: isNum(merged.priceMax) ? merged.priceMax : undefined,
   soldWithinDays: isNum(merged.soldWithinDays) ? merged.soldWithinDays : undefined,
  });

} else if (merged.city && merged.state) {
  // Fall back to city/state text search
  fetchListings(merged);

} else {
  // Last resort
  setListings(onMarketListings);
}
};
const centerWithOffset = (map, { lat, lng }, offsetPx = { x: 280, y: 0 }) => {
  if (!map || lat == null || lng == null) return;
  const zoom = map.getZoom();
  const pt = map.project(L.latLng(lat, lng), zoom);
  const shifted = L.point(pt.x - offsetPx.x, pt.y - offsetPx.y); // push target to the right
  map.setView(map.unproject(shifted, zoom), zoom, { animate: true });
};
// Keep your existing handleSearch; it’s good.
// Add an optional "near me" click handler:
// put this helper above handleNearMe
const getPositionOnce = (opts) =>
  new Promise((resolve, reject) => {
    if (!('geolocation' in navigator)) return reject(new Error('Geolocation not available'));
    navigator.geolocation.getCurrentPosition(resolve, reject, opts);
  });
function fitGeoJSONBounds(geojson) {
  try {
    if (!geojson || !leafletMap) return;
    const layer = L.geoJSON(geojson);
    const b = layer.getBounds();
    console.log("Boundary bounds valid?", b?.isValid?.(), b?.toBBoxString?.());
    if (b.isValid()) {
      const center = b.getCenter();
      centerWithOffset(leafletMap, { lat: center.lat, lng: center.lng }, { x: 280, y: 0 });
      leafletMap.fitBounds(b, { padding: [20, 20] });
    }
  } catch(e) { console.warn("fitGeoJSONBounds failed", e); }
}
const handleNearMe = async () => {
  try {
    // 1) fast, precise try
    const fast = await getPositionOnce({ enableHighAccuracy: true, maximumAge: 10_000, timeout: 6000 });
    const { latitude, longitude } = fast.coords;
    // center + fetch
    setUserLocation({ latitude, longitude });
    setGeoStatus('success');
    setSearchParams(p => ({ ...p, latitude, longitude, search_radius: 30, location: "", page: "1" }));
    setMapCenter([latitude, longitude]);
    if (leafletMap) centerWithOffset(leafletMap, { lat: latitude, lng: longitude }, { x: 280, y: 0 });
await fetchByCoords({
  latitude,
  longitude,
  radius: 30,
  listingType: "For Sale",
  priceType: lastQueryRef.current?.priceType,
  priceMin:  lastQueryRef.current?.priceMin,
  priceMax:  lastQueryRef.current?.priceMax,
});  } catch (e1) {
    // 2) fallback: less strict, longer timeout
    try {
      const slow = await getPositionOnce({ enableHighAccuracy: false, maximumAge: 120_000, timeout: 15_000 });
      const { latitude, longitude } = slow.coords;
      setUserLocation({ latitude, longitude });
      setGeoStatus('success');
      setSearchParams(p => ({ ...p, latitude, longitude, search_radius: 30, location: "", page: "1" }));
      setMapCenter([latitude, longitude]);
      if (leafletMap) centerWithOffset(leafletMap, { lat: latitude, lng: longitude }, { x: 280, y: 0 });
await fetchByCoords({
  latitude,
  longitude,
  radius: 30,
  listingType: "For Sale",
  priceType: lastQueryRef.current?.priceType,
  priceMin:  lastQueryRef.current?.priceMin,
  priceMax:  lastQueryRef.current?.priceMax,
});    } catch (e2) {
      console.warn("Near Me failed:", e1?.message || e1, e2?.message || e2);
      setGeoStatus('error');
      alert("Couldn't get your location. Check browser permissions and that you're on HTTPS or localhost.");
    }
  }
};


const toggleFavorite = async (...args) => {
  // Supports:
  // A) (next, docId, listing) ← from ListingItem (recommended)
  // B) (listing, next)        ← older calls (still works)
  let next, docId, listing;

  if (typeof args[0] === "boolean") {
    // A
    next = !!args[0];
    docId = String(args[1] || "");
    listing = args[2];
  } else {
    // B
    listing = args[0];
    next = !!args[1];
    docId = getDocId(listing);
  }
  if (!docId) return;

  // optimistic update + localStorage mirror
  setFavorites((prev) => {
    const s = new Set(prev.map(String));
    next ? s.add(docId) : s.delete(docId);
    const arr = [...s];
    try { localStorage.setItem("favorites", JSON.stringify(arr)); } catch {}
    return arr;
  });

  // Firestore persistence (mirrors mobile)
  try {
    const fb = window.firebase || undefined;
    const fs = fb?.firestore ? fb.firestore() : undefined;
    if (!fs || !currentUser?.uid) return;
    const ref = fs
      .collection("users")
      .doc(String(currentUser.uid))
      .collection("favorites")
      .doc(docId);
    if (next) await ref.set({ at: Date.now() });
    else await ref.delete();
  } catch (e) {
    console.warn("Favorite persist failed:", e?.message || e);
  }
};

  // Off-market filtering
  const handleOffMarketResults = filtered => setListings(filtered);

  // Listing click
  const handleListingClick = async listing => {
    setSelectedListing(listing);
    const isOff = !listing.formattedAddress;

    if (isOff) {
      setIsOffMarketModalOpen(true);
      try {
        const resp = await fetch(
          'https://api-vasejd2hkq-uc.a.run.app/getListingDetails',
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ listingId: listing.id }),
          }
        );
        const details = await resp.json();
        setListingOffMarketDetails(details);
      } catch (err) {
        console.error('Error off-market details:', err);
      }
    } else {
      setIsModalOpen(true);
    }
  };

  const closeModal = () => setIsModalOpen(false);
  const closeOffMarketModal = () => {
    setIsOffMarketModalOpen(false);
    setListingOffMarketDetails(null);
  };

  const handleToggleOffMarket = () => {
    setShowOffMarket(show => {
      const next = !show;
      setListings(next ? offMarketProperties : onMarketListings);
      return next;
    });
  };
// One source 
// of truth for what to render (map markers + list)

const isListingFavorite = (l, favs) => favs?.includes(getDocId(l));
// 1) one helper so IDs are consistent everywhere

// 2) a catalog of all items you can show

// 4) now decide what to show
const boundaryPolys = useMemo(() => {
  if (!boundaryGeoJSON) return [];
  try { return polysFromGeoJSON(boundaryGeoJSON); }
  catch { return []; }
}, [boundaryGeoJSON]);

const visibleListings = useMemo(() => {
  const base = listings || [];
  const favSet = new Set((favorites || []).map(String));

  // If a boundary is present, keep only listings inside it
  const masked = boundaryPolys.length
    ? base.filter(l => {
        const lat = Number(l?.latitude), lng = Number(l?.longitude);
        if (!Number.isFinite(lat) || !Number.isFinite(lng)) return false;
        return pointInAnyPoly({ latitude: lat, longitude: lng }, boundaryPolys);
      })
    : base;

  return masked.filter(l => {
    const favOK = !showFavoritesOnly || favSet.has(getDocId(l));
    const sfOK  = !sellerFinOnly || !!(l?.sellerFinanced || detectSellerFinancing(l?.remarks || "").isSellerFinanced);
    return favOK && sfOK;
  });
}, [listings, favorites, showFavoritesOnly, sellerFinOnly, boundaryPolys]);
  if (!currentUser) return null;

return (
  <div style={pageStyles.pageContainer}>
    {/* 🔹 Full-width top bar over EVERYTHING (map + sidebar) */}
   {/* 🔹 Top bar over ONLY the map + listings */}
<div style={overlayStyles.topBar}>
  <div style={overlayStyles.topBarInner}>
    <ZillowSearchBar
  onSearch={handleSearch}
  onNearMe={handleNearMe}
  onSelectExactListing={(listing) => setExactListing(listing)}
  onBoundary={(gj) => { setBoundaryGeoJSON(gj); fitGeoJSONBounds(gj); }} // ← NEW
/>
  </div>
</div>
<style>{`.leaflet-control-zoom{display:none!important}`}</style>
    {/* ===== Map (background) + Right Listings ===== */}
    <div style={pageStyles.contentContainer}>
      {/* Full-bleed map background */}
      <div style={zillowStyles.mapBG}>
<MapContainer center={mapCenter} whenCreated={(m) => setLeafletMap(m)} zoom={12} zoomControl={false} style={{height:'100%',width:'100%'}}>
{boundaryGeoJSON && (
  <GeoJSON
    key={
      // ensure a new key when the geometry changes
      (boundaryGeoJSON.features?.[0]?.properties?.GEOID) ||
      (boundaryGeoJSON.features?.[0]?.properties?.ZIP)   ||
      (boundaryGeoJSON.features?.[0]?.properties?.NAME)  ||
      String(Date.now())
    }
    data={boundaryGeoJSON}
    style={boundaryStyle}
  />
)}  {mapStyle === 'classic' ? (
    <TileLayer
 url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"      
 attribution="© OpenStreetMap contributors"
    />
  ) : (
    <>
      <TileLayer
        url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
        attribution="Tiles © Esri"
      />
      <TileLayer
        url="https://server.arcgisonline.com/ArcGIS/rest/services/Reference/World_Boundaries_and_Places/MapServer/tile/{z}/{y}/{x}"
        attribution="© Esri"
        opacity={0.9}
      />
    </>
  )}

  {/* 👇 mount this so setView runs whenever mapCenter changes */}
  <ChangeMapView center={mapCenter} />

 {/* 🔵 Current location dot (if available) */}
 {userLocation && userLocation.latitude && userLocation.longitude && (
   <CircleMarker
     center={[userLocation.latitude, userLocation.longitude]}
     radius={6}
     pathOptions={{ color: '#0A84FF', fillColor: '#0A84FF', fillOpacity: 1, weight: 2 }}
   />
 )}
  {!showOffMarket &&
    visibleListings
      .filter(l => l.latitude != null && l.longitude != null)
      .map(l => (
        <Marker
          key={l.id}
          position={[l.latitude, l.longitude]}
          icon={createMarkerIcon(l, markerLabel)}
          eventHandlers={{ click: () => handleListingClick(l) }}
        />
      ))}
</MapContainer>

        {/* ✅ Bottom-left controls (marker label + map style) */}
     {/* ✅ Bottom-left controls on the map */}
{/* ✅ Bottom-left controls on the map (stacked) */}
<div style={overlayStyles.mapControlsBL}>
  {/* Marker label picker */}
    <div style={overlayStyles.chipGroup}>
      {boundaryGeoJSON && (
  <button
    onClick={() => setBoundaryGeoJSON(null)}
    style={{
      padding:'6px 10px',
      border:'1px solid #ccc',
      borderRadius:4,
      background:'#fff',
      color:'#333',
      cursor:'pointer',
      fontSize:12,
      marginLeft: 8
    }}
    title="Clear ZIP boundary"
  >
    Clear ZIP
  </button>
)}
    <button
      onClick={() => setMapStyle('classic')}
      style={{
        padding:'6px 10px',
        border:'1px solid #ccc',
        borderRadius:4,
        background: mapStyle==='classic' ? '#007aff' : '#fff',
        color: mapStyle==='classic' ? '#fff' : '#333',
        cursor:'pointer',
        fontSize:12
      }}
    >
      Classic
    </button>
    <button
      onClick={() => setMapStyle('satellite')}
      style={{
        padding:'6px 10px',
        border:'1px solid #ccc',
        borderRadius:4,
        background: mapStyle==='satellite' ? '#007aff' : '#fff',
        color: mapStyle==='satellite' ? '#fff' : '#333',
        cursor:'pointer',
        fontSize:12
      }}
    >
      Satellite
    </button>
  </div>
  <div style={overlayStyles.chipGroup}>
    {['price','cashflow','days'].map(type => (
      <button
        key={type}
        onClick={() => setMarkerLabel(type)}
        style={{
          padding:'6px 10px',
          border:'1px solid #ccc',
          borderRadius:4,
          background: markerLabel===type ? '#007aff' : '#fff',
          color: markerLabel===type ? '#fff' : '#333',
          cursor:'pointer',
          textTransform:'capitalize',
          fontSize:12
        }}
      >
        {type}
      </button>
    ))}
  </div>

  {/* Map style toggle */}

</div>
      </div>

      {/* Right listings panel over the map */}
      <aside style={zillowStyles.rightColumn}>
        <div style={zillowStyles.rightGradient} />
        <div style={zillowStyles.rightContent}>
          <div style={{ marginBottom: 10 }}>
            <MarketToggle
              showOffMarket={showOffMarket}
              setShowOffMarket={handleToggleOffMarket}
              offMarketProperties={offMarketProperties}
              onMarketListings={onMarketListings}
              setListings={setListings}
            />
              <button
    onClick={() => setShowFavoritesOnly(v => !v)}
    title={showFavoritesOnly ? 'Showing favorites' : 'Show favorites only'}
    style={{
      flex:'0 0 auto',
      display:'inline-flex',
      alignItems:'center',
      gap:6,
      padding:'8px 10px',
      border:'1px solid ' + (showFavoritesOnly ? '#e63946' : '#ccc'),
      background: showFavoritesOnly ? 'rgba(230,57,70,0.10)' : '#fff',
      color: showFavoritesOnly ? '#e63946' : '#333',
      borderRadius: 8,
      cursor:'pointer',
      fontSize: 13,
      fontWeight: 600
    }}
  >
    <svg width="16" height="16" viewBox="0 0 24 24" fill={showFavoritesOnly ? '#e63946' : 'none'} stroke={showFavoritesOnly ? '#e63946' : '#333'} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M20.8 4.6a5.5 5.5 0 0 0-7.8 0L12 5.6l-1-1a5.5 5.5 0 0 0-7.8 7.8l1 1L12 22l7.8-8.6 1-1a5.5 5.5 0 0 0 0-7.8z"/>
    </svg>
    {showFavoritesOnly ? 'Favorites Only' : 'Favorites'}
  </button>
          </div>

          {showOffMarket && (
            <div style={pageStyles.offMarketSearchBarWrapper}>
              <OffMarketSearchBar
                offMarketProperties={offMarketProperties}
                onSearch={handleOffMarketResults}
              />
            </div>
          )}

          <ul style={zillowStyles.listingsUl}>
            {visibleListings.map(l => (
              <ListingItem
              key={getDocId(l)}
              listing={l}
              mortgage1={Math.round(l.price * 0.0065)}
              onClick={() => handleListingClick(l)}
              isFavorite={isListingFavorite(l, favorites)}
              onToggleFavorite={toggleFavorite}
            />
            ))}
          </ul>
        </div>
      </aside>
    </div>

    {/* ===== Modals ===== */}
    {isModalOpen && <OfferModal listing={selectedListing} onClose={closeModal} />}
    {exactListing && <OfferModal listing={exactListing} onClose={() => setExactListing(null)} />}
    {isOffMarketModalOpen && (
      <OffMarketModal
        listing={selectedListing}
        details={listingOffMarketDetails || {}}
        onClose={closeOffMarketModal}
      />
    )}
  </div>
);
};

export default ZillowClonePage;

// … your component’s closing brace and export here …

// ─────────── Styles ───────────
// ─────────── Styles (replace these keys) ───────────

const boundaryStyle = {
  color: 'rgba(0,122,255,0.9)',
  weight: 2,
  fillColor: 'rgba(0,122,255,0.15)',
  fillOpacity: 1,
};


const pageStyles = {
  pageContainer: {
    position:'relative',
    display:'flex',
    flexDirection:'column',
    height:'100vh',
    width:'100%',
    background:'transparent',
  },
  contentContainer: {
    position:'relative',
    height:'100%',
    width:'100%',
    overflow:'hidden',
    paddingTop: 72,            // keep content clear of the fixed top bar
    boxSizing:'border-box',
  },
  offMarketSearchBarWrapper: { width:'100%' },
};

const zillowStyles = {
  mapBG: { position:'absolute', inset:0, zIndex:0 },

  rightColumn: {
    position:'relative',
    zIndex:2,
    width:'40vw',
    minWidth: 420,
    marginLeft:'auto',
    height:'100%',
    overflow:'hidden',
  },

  rightGradient: {
    position:'absolute',
    inset:0,
    zIndex:0,
    background:
      'linear-gradient(to left, rgba(0,123,255,0.16) 0%, rgba(0,123,255,0.10) 30%, rgba(0,123,255,0.06) 60%, rgba(0,123,255,0.00) 100%)',
    backdropFilter:'blur(2px)',
    WebkitBackdropFilter:'blur(2px)',
    pointerEvents:'none',
  },

  rightContent: {
    position:'relative',
    zIndex:1,
    height:'100%',
    overflowY:'auto',
    padding:'16px 16px 20px',
    boxSizing:'border-box',
    background:'transparent',
  },

  listingsUl: {
    display:'grid',
    gridTemplateColumns:'repeat(2, minmax(0, 1fr))', // back to two columns
    gap:16,
    listStyle:'none',
    padding:0,
    margin:0,
    background:'transparent',
  },
};

const overlayStyles = {
  topBar: {
    position: 'absolute',   // ← scoped to contentContainer
    top: 0,
    left: 0,
    right: 0,
    zIndex: 3000,
    background: 'linear-gradient(to bottom, rgba(255,255,255,0.92) 0%, rgba(255,255,255,0.45) 45%, rgba(255,255,255,0) 100%)',
    padding: 0,
    pointerEvents: 'none',
  },
mapControlsBL : {
  position:'absolute',
  left:10,
  bottom:10,
  zIndex:2000,
  display:'flex',
  flexDirection:'column', // stack
  gap:8,
  alignItems:'flex-start',
  pointerEvents:'auto'
},
chipGroup : {
  background:'rgba(255,255,255,0.9)',
  borderRadius:6,
  padding:6,
  display:'flex',
  gap:6
},
  
  topBarInner: {
    width: '100%',
    padding: '10px 12px',
    boxSizing: 'border-box',
    pointerEvents: 'auto',
  },
  // keep your other overlay styles as-is
};
  